<template>
  <b-row>
    <b-col sm="12">
      <div class="task-list-view">
        <b-card body-class="py-2">
          <section class="list-view-heading">
            <ModuleViewHeading
              :module-id="Modules.TAREAS"
              :profile-id="Profiles.PERSONAL"
              reference="crudTasks"
              summary-only
            />
            <div class="list-view-heading__controls">
              <div class="list-view-heading__actions">
                <TabHeaderActions />
              </div>
              <div class="list-view-heading__controls-end">
                <b-button
                  variant="link"
                  class="list-view-icon-btn"
                  :class="{ 'list-view-icon-btn--active': filtersOpen }"
                  v-b-tooltip.hover.bottom
                  :title="filtersOpen ? 'Ocultar filtros' : 'Mostrar filtros'"
                  :aria-label="filtersOpen ? 'Ocultar filtros' : 'Mostrar filtros'"
                  @click="filtersOpen = !filtersOpen"
                >
                  <AppIcon name="filter" :fill="filtersOpen" />
                </b-button>
                <b-dropdown
                  right
                  no-caret
                  variant="link"
                  boundary="window"
                  toggle-class="list-view-icon-btn list-view-icon-btn--primary"
                  v-b-tooltip.hover.bottom
                  title="Agregar tarea"
                >
                  <template #button-content><AppIcon name="add" label="Agregar tarea" /></template>
                  <b-dropdown-item @click="openCreate('personal')"><AppIcon name="person" /> Personal</b-dropdown-item>
                  <b-dropdown-item v-if="canCreateCustomerTask" @click="openCreate('customer')"><AppIcon name="business" /> Para cliente</b-dropdown-item>
                  <b-dropdown-item v-if="canCreateSupplierTask" @click="openCreate('supplier')"><AppIcon name="local_shipping" /> Para proveedor</b-dropdown-item>
                  <b-dropdown-item v-if="canCreateProjectTask" @click="openCreate('project')"><AppIcon name="folder" /> Para proyecto</b-dropdown-item>
                </b-dropdown>
              </div>
            </div>
          </section>

          <b-collapse v-model="filtersOpen">
            <div class="list-view-toolbar__filters">
              <div class="list-view-filter task-filter-search">
                <label class="filter-label">Buscar</label>
                <b-form-input v-model.trim="filters.search" size="sm" placeholder="# de tarea, título o descripción" @keydown.enter.prevent="load" />
              </div>
              <div class="list-view-filter task-filter-status">
                <label class="filter-label">Finalización</label>
                <b-form-select v-model="filters.completionStatus" size="sm" :options="completionStatusOptions" />
              </div>
              <div class="list-view-filter task-filter-status">
                <label class="filter-label">Archivo</label>
                <b-form-select v-model="filters.archiveStatus" size="sm" :options="archiveStatusOptions" @change="onArchiveStatusChange" />
              </div>
              <div class="list-view-filter task-filter-type">
                <label class="filter-label">Tipo</label>
                <b-form-select v-model="filters.relationType" size="sm" :options="relationTypeOptions" @change="onRelationTypeChange" />
              </div>
              <div v-if="filters.relationType" class="list-view-filter task-filter-category">
                <label class="filter-label">Categoría</label>
                <b-form-select v-model="filters.category" size="sm" :options="filterCategoryOptions" />
              </div>
              <div class="list-view-filter task-filter-priority">
                <label class="filter-label">Prioridad</label>
                <b-form-select v-model="filters.priority" size="sm" :options="priorityFilterOptions" />
              </div>
              <div v-if="canFilterByAssigned" class="list-view-filter task-filter-assigned">
                <label class="filter-label">Asignado</label>
                <b-form-select v-model="filters.assigned" size="sm" :options="assignedFilterOptions" />
              </div>
              <b-button
                variant="outline-dark"
                size="sm"
                class="task-filter-apply"
                v-b-tooltip.hover
                title="Aplicar filtros"
                @click="load"
              >
                <AppIcon name="filter" />
              </b-button>
            </div>
          </b-collapse>
        </b-card>

        <div class="task-workspace" ref="taskWorkspace" :style="workspaceMinHeightStyle">
          <aside v-if="contextPanelOpen" class="task-context-panel">
            <div class="task-context-panel__header">
              <span><AppIcon :name="selectedContextType.icon" /> {{ selectedContextType.label }}</span>
              <b-button variant="link" class="task-context-panel__toggle" @click="contextPanelOpen = false" v-b-tooltip.hover title="Ocultar panel">
                <AppIcon name="chevron_left" />
              </b-button>
            </div>

            <div class="task-context-tabs">
              <button
                v-for="type in contextTypes"
                :key="'task-context-type-' + type.value"
                type="button"
                :class="{ 'task-context-tabs__btn--active': contextType == type.value }"
                class="task-context-tabs__btn"
                @click="selectContextType(type.value)"
                v-b-tooltip.hover
                :title="type.label"
              >
                <AppIcon :name="type.icon" :fill="contextType == type.value" />
              </button>
            </div>

            <b-input-group v-if="contextType != 'mine'" size="sm" class="task-context-search">
              <b-input-group-prepend is-text><AppIcon name="search" /></b-input-group-prepend>
              <b-form-input v-model.trim="contextSearch" placeholder="Buscar en este listado" />
            </b-input-group>

            <div class="task-context-list" :aria-busy="contextLoading">
              <div v-if="contextLoading" class="task-context-loader" role="status" aria-live="polite">
                <b-spinner small variant="secondary" />
                <span>Cargando contextos…</span>
              </div>
              <template v-else>
                <button
                v-for="node in filteredContextNodes"
                :key="'task-context-node-' + contextType + '-' + node.id"
                type="button"
                class="task-context-node"
                :class="{
                  'task-context-node--active': isContextNodeActive(node),
                  'task-context-node--urgent': node.urgentCount,
                }"
                @click="selectContextNode(node)"
              >
                <span class="task-context-node__avatar" v-if="node.avatar">
                  <b-avatar :src="node.image" size="28px" v-if="node.image"></b-avatar>
                  <b-avatar
                    v-else
                    size="28px"
                    :text="avatarFromName(node.label).text"
                    :style="avatarFromName(node.label).style"
                  ></b-avatar>
                </span>
                <span class="task-context-node__icon" v-else><AppIcon :name="node.icon || selectedContextType.icon" /></span>
                <span class="task-context-node__body">
                  <strong>{{ node.label }}</strong>
                  <small v-if="node.secondaryMeta">{{ node.secondaryMeta }}</small>
                  <small>
                    {{ node.meta || selectedContextType.label }}
                    <AppIcon v-if="node.urgentCount" name="priority_high" class="task-context-node__urgent-icon" />
                  </small>
                </span>
                <b-badge variant="light" pill class="task-context-node__count" v-if="node.count !== null">{{ node.count }}</b-badge>
              </button>
              <EmptyState v-if="!filteredContextNodes.length" icon="search" title="Sin opciones" description="No hay registros para este contexto." />
              </template>
            </div>
          </aside>

          <div v-else class="task-context-collapsed" @click="contextPanelOpen = true" v-b-tooltip.hover.right title="Mostrar panel">
            <AppIcon name="chevron_right" />
            <span>Contextos</span>
          </div>

          <main class="task-results" :class="{ 'task-results--expanded': !contextPanelOpen }">
            <b-card no-body class="mt-2 task-results-card">
              <b-table
                v-if="items.length || loading"
                class="app-table task-list-table"
                responsive
                :items="items"
                :fields="tableFields"
                :stacked="isMobileViewport ? 'md' : false"
                :busy="loading"
                :tbody-tr-class="rowClass"
                @row-clicked="openDetail"
              >
                <template #table-busy>
                  <div class="task-table-loader" role="status" aria-live="polite">
                    <b-spinner small variant="secondary" />
                    <span>Cargando tareas…</span>
                  </div>
                </template>
                <template v-slot:cell(title)="data">
                  <div class="task-title-cell">
                    <div class="task-title-cell__heading">
                      <strong>{{ taskLabel(data.item) }}</strong>
                      <span class="task-title-cell__icons" v-if="taskImages(data.item).length">
                        <AppIcon name="image" />
                        {{ taskImages(data.item).length }}
                      </span>
                      <span v-if="taskReminder(data.item)" class="task-title-cell__icons task-title-cell__icons--reminder" v-b-tooltip.hover title="Tiene un recordatorio configurado">
                        <AppIcon name="notifications" />
                      </span>
                    </div>
                    <small v-if="data.item.description">{{ taskDescriptionText(data.item) }}</small>
                  </div>
                </template>

                <template v-slot:cell(checklist_progress)="data">
                  <div v-if="hasChecklist(data.item)" class="task-checklist-progress" :class="'task-checklist-progress--' + data.item.checklist_status">
                    <b-progress :value="data.item.checklist_progress" :max="100" height="6px" />
                    <span>{{ data.item.checklist_progress }}%</span>
                  </div>
                  <span v-else class="text-muted">—</span>
                </template>

                <template v-slot:cell(priority)="data">
                  <span
                    class="task-priority-dot"
                    :class="'task-priority-dot--' + data.item.priority"
                    v-b-tooltip.hover
                    :title="priorityOptionFor(data.item.priority).label"
                  >
                    <AppIcon :name="priorityOptionFor(data.item.priority).icon" />
                  </span>
                </template>

                <template v-slot:cell(status)="data">
                  <b-badge :variant="statusVariant(data.item)">{{ statusLabel(data.item) }}</b-badge>
                </template>

                <template v-slot:cell(category)="data">
                  <span v-if="data.item.category" class="task-category-pill">{{ data.item.category }}</span>
                  <span v-else class="text-muted">Sin categoría</span>
                </template>

                <template v-slot:cell(relation)="data">
                  <div class="task-relation-cell">
                    <template v-if="relationEntity(data.item)">
                      <b-avatar :src="relationEntity(data.item).image" size="24px" v-if="relationEntity(data.item).image"></b-avatar>
                      <b-avatar
                        v-else
                        size="24px"
                        :text="avatarFromName(relationValue(data.item)).text"
                        :style="avatarFromName(relationValue(data.item)).style"
                      ></b-avatar>
                    </template>
                    <span class="task-relation-cell__icon" v-else>
                      <AppIcon :name="relationIcon(data.item)" />
                    </span>
                    <span class="task-relation-cell__body">
                      <small>{{ relationTitle(data.item) }}</small>
                      <strong>{{ relationValue(data.item) }}</strong>
                    </span>
                  </div>
                </template>

                <template v-slot:cell(people)="data">
                  <div class="task-people-cell">
                    <span>
                      <b-avatar :src="staffImage(taskResponsible(data.item))" size="22px" v-if="staffImage(taskResponsible(data.item))"></b-avatar>
                      <b-avatar v-else size="22px" :text="avatarFromName(staffName(taskResponsible(data.item))).text" :style="avatarFromName(staffName(taskResponsible(data.item))).style"></b-avatar>
                      {{ staffName(taskResponsible(data.item)) }}
                    </span>
                  </div>
                </template>

                <template v-slot:cell(created_at)="data">
                  <div class="task-date-cell">
                    <strong>{{ humanDate(data.item.created_at) }}</strong>
                    <small>{{ formatDate(data.item.created_at) }}</small>
                  </div>
                </template>

                <template v-slot:cell(actions)="data">
                  <b-dropdown
                    right
                    no-caret
                    size="sm"
                    variant="link"
                    boundary="window"
                    :toggle-class="isMobileViewport ? 'task-action-toggle-labeled' : 'app-table-action-toggle'"
                    :class="{ 'pull-right': !isMobileViewport }"
                    @click.stop
                  >
                    <template #button-content>
                      <AppIcon name="more_vert" :label="isMobileViewport ? 'Opciones' : 'Acciones'" :class="{ 'mr-1': isMobileViewport }" />
                      <span v-if="isMobileViewport">Opciones</span>
                    </template>
                    <template v-if="canReorderTasks && canManuallyOrderTask(data.item) && hasMovableTask(data.item)">
                      <b-dropdown-item v-if="canMoveTask(data.item, 'up')" :disabled="movingTaskId == data.item.id" @click="moveTask(data.item, 'up')">
                        <AppIcon name="arrow_upward" /> Subir
                      </b-dropdown-item>
                      <b-dropdown-item v-if="canMoveTask(data.item, 'down')" :disabled="movingTaskId == data.item.id" @click="moveTask(data.item, 'down')">
                        <AppIcon name="arrow_downward" /> Bajar
                      </b-dropdown-item>
                      <b-dropdown-divider />
                    </template>
                    <b-dropdown-item v-if="canEditTask(data.item)" @click="openEdit(data.item)"><AppIcon name="edit" /> Editar</b-dropdown-item>
                    <b-dropdown-item v-if="canEditTask(data.item)" @click="openChangeRelation(data.item)"><AppIcon name="account_tree" /> Cambiar tipo</b-dropdown-item>
                    <b-dropdown-item v-if="canAddProjectHours(data.item)" @click="openProjectHoursCompletion(data.item, 'add')"><AppIcon name="schedule" /> Cargar horas</b-dropdown-item>
                    <b-dropdown-item v-if="canEditTask(data.item)" @click="toggleStatusManagement(data.item)">
                      <AppIcon :name="managesStatus(data.item) ? 'repeat' : 'check_circle'" />
                      {{ managesStatus(data.item) ? 'Convertir en tarea permanente' : 'Permitir gestionar estados' }}
                    </b-dropdown-item>
                    <b-dropdown-item @click="openDuplicate(data.item)"><AppIcon name="content_copy" /> Duplicar</b-dropdown-item>
                    <b-dropdown-divider v-if="canCreateTaskReminder(data.item) || canDeleteTaskReminder(data.item)" />
                    <b-dropdown-item v-if="canCreateTaskReminder(data.item)" @click="createReminder(data.item)"><AppIcon name="notifications" /> Crear recordatorio</b-dropdown-item>
                    <b-dropdown-item v-if="canDeleteTaskReminder(data.item)" class="text-warning" @click="removeTaskReminder(data.item)"><AppIcon name="notifications_off" class="text-warning" /> Quitar recordatorio</b-dropdown-item>
                    <b-dropdown-divider v-if="!data.item.archived && managesStatus(data.item) && (canCompleteTask(data.item) || data.item.done)" />
                    <b-dropdown-item v-if="!data.item.archived && canCompleteTask(data.item)" @click="toggleComplete(data.item)">
                      <AppIcon :name="data.item.done ? 'undo' : 'check2-circle'" :class="{ 'text-success': !data.item.done }" />
                      {{ data.item.done ? 'Marcar pendiente' : 'Completar' }}
                    </b-dropdown-item>
                    <b-dropdown-item v-if="managesStatus(data.item) && data.item.done && !data.item.archived" @click="archiveTask(data.item)">
                      <AppIcon name="archive" /> Archivar
                    </b-dropdown-item>
                    <b-dropdown-item v-if="managesStatus(data.item) && data.item.archived" @click="restoreTask(data.item)">
                      <AppIcon name="undo" /> Restaurar
                    </b-dropdown-item>
                    <template v-if="canEditTask(data.item)">
                      <b-dropdown-divider />
                      <b-dropdown-item @click="removeTask(data.item)" class="text-danger"><AppIcon name="delete" class="text-danger" /> Eliminar</b-dropdown-item>
                    </template>
                  </b-dropdown>
                </template>
              </b-table>

              <EmptyState v-else-if="!loading" icon="tasks" title="No hay tareas" description="No se encontraron tareas para los filtros seleccionados." />

              <div v-if="!loading" class="app-table-footer task-table-footer">
                <span class="list-view-toolbar__count">{{ totalItems }} registros</span>
                <nav v-if="totalItems > perPage">
                  <b-pagination
                    v-model="currentPage"
                    class="pull-right mb-0"
                    size="sm"
                    pills=""
                    :total-rows="totalItems"
                    :per-page="perPage"
                  />
                </nav>
              </div>
            </b-card>
          </main>
        </div>

        <b-sidebar v-model="detailDrawer" right shadow backdrop width="440px" class="task-detail-sidebar" @hidden="closeDetailFromRoute">
          <template #title>
            <span class="task-sidebar-title"><AppIcon name="tasks" /> Detalle de tarea</span>
          </template>
          <div v-if="itemSelected" class="list-view-drawer task-detail">
            <div class="list-view-drawer__header">
              <div class="list-view-drawer__title">
                <span class="list-view-drawer__code">#{{ itemSelected.id }}</span>
                <div>{{ taskTitle(itemSelected) }}</div>
                <div class="list-view-drawer__meta">{{ humanDate(itemSelected.created_at) }} · {{ relationTitle(itemSelected) }}: {{ relationValue(itemSelected) }}</div>
              </div>
              <div class="list-view-drawer__nav">
                <b-button size="sm" variant="outline-secondary"
                          :disabled="!canGoPrevTask"
                          v-b-tooltip.hover title="Tarea anterior"
                          @click="goPrevTask">
                  <AppIcon name="chevron-up" label="Tarea anterior" />
                </b-button>
                <b-button size="sm" variant="outline-secondary"
                          :disabled="!canGoNextTask"
                          v-b-tooltip.hover title="Tarea siguiente"
                          @click="goNextTask">
                  <AppIcon name="chevron-down" label="Tarea siguiente" />
                </b-button>
              </div>
            </div>

            <div class="list-view-drawer__stats">
              <div class="list-view-drawer__stat" :class="'list-view-drawer__stat--' + statusVariant(itemSelected)">
                <span class="list-view-drawer__stat-label">Estado</span>
                <span class="list-view-drawer__stat-value">{{ statusLabel(itemSelected) }}</span>
              </div>
              <div class="list-view-drawer__stat task-reminder-stat" :class="{ 'task-reminder-stat--active': taskReminder(itemSelected) }">
                <span class="list-view-drawer__stat-label">Recordatorio</span>
                <span class="list-view-drawer__stat-value task-reminder-stat__value" :class="{ 'text-muted': !taskReminder(itemSelected) }">
                  <AppIcon :name="taskReminder(itemSelected) ? 'notifications' : 'notifications_off'" />
                  <span class="task-reminder-stat__date">
                    <strong>{{ taskReminder(itemSelected) ? formatDate(taskReminder(itemSelected).remind_at) : 'Sin recordatorio' }}</strong>
                    <small v-if="taskReminder(itemSelected)">{{ humanDate(taskReminder(itemSelected).remind_at) }}</small>
                  </span>
                </span>
              </div>
            </div>

            <div v-if="!itemSelected.archived && canCompleteTask(itemSelected)" class="task-detail__quick-action">
              <b-button
                block
                :variant="itemSelected.done ? 'outline-secondary' : 'success'"
                @click="toggleComplete(itemSelected)"
              >
                <AppIcon :name="itemSelected.done ? 'undo' : 'check2-circle'" class="mr-1" />
                {{ itemSelected.done ? 'Marcar como pendiente' : 'Completar tarea' }}
              </b-button>
            </div>

            <div>
              <div class="list-view-drawer__section-title">
                <AppIcon name="flag" /> Prioridad
              </div>
              <b-dropdown
                no-caret
                boundary="window"
                variant="light"
                size="sm"
                class="task-priority-drawer-dropdown"
                :disabled="!canEditTask(itemSelected)"
                :toggle-class="['task-priority-toggle', 'task-priority-toggle--' + itemSelected.priority]"
              >
                <template #button-content>
                  <AppIcon :name="priorityOptionFor(itemSelected.priority).icon" />
                  {{ priorityOptionFor(itemSelected.priority).label }}
                  <AppIcon name="expand_more" class="task-priority-toggle__caret" v-if="canEditTask(itemSelected)" />
                </template>
                <b-dropdown-item
                  v-for="option in priorityOptions"
                  :key="'sidebar-priority-option-' + option.value"
                  @click="changePriority(itemSelected, option.value)"
                >
                  <span
                    class="task-priority-menu-item"
                    :class="['task-priority-menu-item--' + option.value, { 'task-priority-menu-item--active': itemSelected.priority == option.value }]"
                  >
                    <AppIcon :name="option.icon" />
                    {{ option.label }}
                    <AppIcon name="check" class="task-priority-menu-item__check" v-if="itemSelected.priority == option.value" />
                  </span>
                </b-dropdown-item>
              </b-dropdown>
            </div>

            <div v-if="itemSelected.project_id">
              <div class="list-view-drawer__section-title task-hours-section-title">
                <span><AppIcon name="schedule" /> Horas vinculadas</span>
                <b-button v-if="canAddProjectHours(itemSelected)" size="sm" variant="outline-secondary" @click="openProjectHoursCompletion(itemSelected, 'add')"><AppIcon name="add" /> Cargar</b-button>
              </div>
              <div v-if="taskProjectHours(itemSelected).length" class="task-hours-list">
                <div v-for="hour in taskProjectHours(itemSelected)" :key="hour.id" class="task-hours-list__item">
                  <div class="task-hours-list__copy">
                    <div class="task-hours-list__headline"><strong>{{ hour.hours }} hs · {{ formatHourDate(hour.date) }}</strong><span class="task-hours-list__status" :class="'task-hours-list__status--' + hourBillingStatus(hour)">{{ hourBillingLabel(hour) }}</span></div>
                    <small>{{ hour.description || 'Sin detalle' }}</small>
                    <span v-if="hourStaff(hour)" class="task-hours-list__author"><b-avatar :src="staffImage(hourStaff(hour))" size="18px" v-if="staffImage(hourStaff(hour))"></b-avatar><b-avatar v-else size="18px" :text="avatarFromName(staffName(hourStaff(hour))).text" :style="avatarFromName(staffName(hourStaff(hour))).style"></b-avatar>Consumida por {{ staffName(hourStaff(hour)) }}</span>
                  </div>
                  <b-dropdown v-if="canManageProjectHour(itemSelected, hour)" right no-caret size="sm" variant="link" toggle-class="app-table-action-toggle">
                    <template #button-content><AppIcon name="more_vert" label="Acciones de hora" /></template>
                    <b-dropdown-item @click="openEditProjectHours(itemSelected, hour)"><AppIcon name="edit" /> Editar</b-dropdown-item>
                    <b-dropdown-item @click="unlinkProjectHours(itemSelected, hour)"><AppIcon name="link_off" /> Desvincular</b-dropdown-item>
                    <b-dropdown-item class="text-danger" @click="removeProjectHours(itemSelected, hour)"><AppIcon name="delete" class="text-danger" /> Eliminar</b-dropdown-item>
                  </b-dropdown>
                </div>
              </div>
              <EmptyState v-else icon="schedule" title="Sin horas cargadas" description="Registrá las iteraciones de trabajo realizadas en esta tarea." />
            </div>

            <div>
              <div class="list-view-drawer__section-title">
                <AppIcon name="account_tree" /> Contexto
              </div>
              <div class="app-modal-detail-list">
                <div class="app-modal-detail-list__row">
                  <span class="app-modal-detail-list__label">{{ relationTitle(itemSelected) }}</span>
                  <span class="app-modal-detail-list__value task-detail-person">
                    <template v-if="relationEntity(itemSelected)">
                      <b-avatar :src="relationEntity(itemSelected).image" size="25px" v-if="relationEntity(itemSelected).image"></b-avatar>
                      <b-avatar
                        v-else
                        size="25px"
                        :text="avatarFromName(relationValue(itemSelected)).text"
                        :style="avatarFromName(relationValue(itemSelected)).style"
                      ></b-avatar>
                    </template>
                    {{ relationValue(itemSelected) }}
                  </span>
                </div>
                <div class="app-modal-detail-list__row" v-if="itemSelected.category">
                  <span class="app-modal-detail-list__label">Categoría</span>
                  <span class="app-modal-detail-list__value">{{ itemSelected.category }}</span>
                </div>
                <div class="app-modal-detail-list__row" v-if="itemSelected.project_id">
                  <span class="app-modal-detail-list__label">Horas de la tarea</span>
                  <span class="app-modal-detail-list__value">
                    <AppIcon name="schedule" />
                    {{ projectHoursLabel(itemSelected) }}
                  </span>
                </div>
              </div>
            </div>

            <div>
              <div class="list-view-drawer__section-title task-hours-section-title">
                <span><AppIcon name="people" /> Personas</span>
                <b-button size="sm"
                          :variant="taskCommentsCount ? 'outline-primary' : 'outline-secondary'"
                          @click="showTaskComments = true">
                  <AppIcon name="chat_bubble" class="mr-1" /> Comentarios
                  <b-badge v-if="taskCommentsCount" variant="primary" pill class="ml-1">{{ taskCommentsCount }}</b-badge>
                </b-button>
              </div>
              <div class="app-modal-detail-list">
                <div class="app-modal-detail-list__row">
                  <span class="app-modal-detail-list__label">Responsable</span>
                  <span class="app-modal-detail-list__value task-detail-person">
                    <b-avatar :src="staffImage(taskResponsible(itemSelected))" size="25px" v-if="staffImage(taskResponsible(itemSelected))"></b-avatar>
                    <b-avatar v-else size="25px" :text="avatarFromName(staffName(taskResponsible(itemSelected))).text" :style="avatarFromName(staffName(taskResponsible(itemSelected))).style"></b-avatar>
                    {{ staffName(taskResponsible(itemSelected)) }}
                  </span>
                </div>
                <div class="app-modal-detail-list__row">
                  <span class="app-modal-detail-list__label">Propietario</span>
                  <span class="app-modal-detail-list__value task-detail-person">
                    <b-avatar :src="staffImage(itemSelected.creator)" size="25px" v-if="staffImage(itemSelected.creator)"></b-avatar>
                    <b-avatar v-else size="25px" :text="avatarFromName(staffName(itemSelected.creator)).text" :style="avatarFromName(staffName(itemSelected.creator)).style"></b-avatar>
                    {{ staffName(itemSelected.creator) }}
                  </span>
                </div>
              </div>
            </div>

            <div>
              <div class="list-view-drawer__section-title">
                <AppIcon name="schedule" /> Fechas
              </div>
              <div class="app-modal-detail-list">
              <div class="app-modal-detail-list__row">
                  <span class="app-modal-detail-list__label">Creada</span>
                  <span class="app-modal-detail-list__value">
                    {{ humanDate(itemSelected.created_at) }}
                    <div class="task-detail-date-sub">{{ formatDate(itemSelected.created_at) }}</div>
                  </span>
                </div>
                <div class="app-modal-detail-list__row" v-if="itemSelected.done_at">
                  <span class="app-modal-detail-list__label">Completada</span>
                  <span class="app-modal-detail-list__value">
                    {{ humanDate(itemSelected.done_at) }}
                    <div class="task-detail-date-sub">{{ formatDate(itemSelected.done_at) }}</div>
                  </span>
                </div>
                <div class="app-modal-detail-list__row" v-if="itemSelected.archived_at">
                  <span class="app-modal-detail-list__label">Archivada</span>
                  <span class="app-modal-detail-list__value">
                    {{ humanDate(itemSelected.archived_at) }}
                    <div class="task-detail-date-sub">{{ formatDate(itemSelected.archived_at) }}</div>
                  </span>
                </div>
              </div>
            </div>

            <div>
              <div class="list-view-drawer__section-title">
                <AppIcon name="notes" /> Detalle
              </div>
              <div v-if="detailChecklistStatus" class="task-checklist-alert" :class="'task-checklist-alert--' + detailChecklistStatus">
                <AppIcon :name="checklistStatusIcon(detailChecklistStatus)" />
                {{ checklistStatusLabel(detailChecklistStatus) }}{{ hasChecklist(itemSelected) ? ' · ' + itemSelected.checklist_progress + '%' : '' }}
              </div>
              <div v-if="itemSelected.description" class="task-detail__description">
                <ChecklistViewer
                  :value="itemSelected.description"
                  :title="taskLabel(itemSelected)"
                  :editable="canEditTask(itemSelected)"
                  @input="onDescriptionChange($event)"
                />
              </div>
              <EmptyState v-else icon="notes" title="Sin detalle cargado" />
            </div>

            <div>
              <div class="list-view-drawer__section-title">
                <AppIcon name="attach_file" /> Archivos adjuntos
              </div>
              <div v-if="taskFiles(itemSelected).length" class="task-detail__files">
                <div v-for="(file, index) in taskFiles(itemSelected)" :key="file.path || file.name + index" class="task-detail__file">
                  <a :href="file.url || file.path" target="_blank" rel="noopener">
                    <AppIcon name="attach_file" />
                    <span>{{ file.name || 'Archivo adjunto' }}</span>
                    <small>{{ fileSize(file.size) }}</small>
                  </a>
                  <audio v-if="isAudioFile(file)" :src="file.url || file.path" controls preload="metadata"></audio>
                </div>
              </div>
              <EmptyState v-else icon="attach_file" title="Sin archivos adjuntos" />
            </div>

            <div>
              <div class="list-view-drawer__section-title">
                <AppIcon name="image" /> Imágenes
              </div>
              <div class="task-detail__images" v-if="taskImages(itemSelected).length">
                <a v-for="image in taskImages(itemSelected)" :key="image.url || image.path" :href="image.url || image.path" target="_blank">
                  <img :src="image.url || image.path" alt="Imagen de tarea" />
                </a>
              </div>
              <EmptyState v-else icon="image" title="Sin imágenes" />
            </div>

            <div class="list-view-drawer__actions">
              <b-button size="sm" variant="outline-secondary" v-if="canEditTask(itemSelected)" @click="openEdit(itemSelected)">
                <AppIcon name="edit" class="mr-1" /> Editar
              </b-button>
              <b-button size="sm" variant="outline-secondary" v-if="canEditTask(itemSelected)" @click="openChangeRelation(itemSelected)">
                <AppIcon name="account_tree" class="mr-1" /> Cambiar tipo
              </b-button>
              <b-button size="sm" variant="outline-secondary" v-if="canAddProjectHours(itemSelected)" @click="openProjectHoursCompletion(itemSelected, 'add')">
                <AppIcon name="schedule" class="mr-1" /> Cargar horas
              </b-button>
              <b-button size="sm" variant="outline-secondary" v-if="canEditTask(itemSelected)" @click="toggleStatusManagement(itemSelected)">
                <AppIcon :name="managesStatus(itemSelected) ? 'repeat' : 'check_circle'" class="mr-1" />
                {{ managesStatus(itemSelected) ? 'Hacer permanente' : 'Gestionar estados' }}
              </b-button>
              <b-button size="sm" variant="outline-secondary" v-if="canCreateTaskReminder(itemSelected)" @click="createReminder(itemSelected)">
                <AppIcon name="notifications" class="mr-1" /> Crear recordatorio
              </b-button>
              <b-button size="sm" variant="outline-warning" v-if="canDeleteTaskReminder(itemSelected)" @click="removeTaskReminder(itemSelected)">
                <AppIcon name="notifications_off" class="mr-1" /> Quitar recordatorio
              </b-button>
              <b-button size="sm" variant="outline-secondary" @click="openDuplicate(itemSelected)">
                <AppIcon name="content_copy" class="mr-1" /> Duplicar
              </b-button>
              <b-button size="sm" variant="outline-secondary" v-if="managesStatus(itemSelected) && itemSelected.done && !itemSelected.archived" @click="archiveTask(itemSelected)">
                <AppIcon name="archive" class="mr-1" /> Archivar
              </b-button>
              <b-button size="sm" variant="outline-secondary" v-if="managesStatus(itemSelected) && itemSelected.archived" @click="restoreTask(itemSelected)">
                <AppIcon name="undo" class="mr-1" /> Restaurar
              </b-button>
            </div>
          </div>

          <template #footer>
            <div class="list-view-drawer__footer" v-if="itemSelected && canEditTask(itemSelected)">
              <b-button size="sm" variant="outline-danger" block @click="removeTask(itemSelected)">
                <AppIcon name="delete" class="mr-1" /> Eliminar
              </b-button>
            </div>
          </template>
        </b-sidebar>

        <b-modal v-model="showTaskComments" size="lg" hide-footer hide-header-close @hidden="onCloseTaskComments">
          <template #modal-header="{ close }">
            <div class="app-modal-header">
              <span class="app-modal-header__icon"><AppIcon name="chat_bubble" /></span>
              <h5 class="app-modal-header__title">Comentarios{{ itemSelected ? ' · ' + taskLabel(itemSelected) : '' }}</h5>
              <b-button variant="link" class="app-modal-header__close" @click="close"><AppIcon name="close" label="Cerrar" /></b-button>
            </div>
          </template>
          <TaskComments v-if="itemSelected && showTaskComments"
                        :task-id="itemSelected.id"
                        :task-title="taskLabel(itemSelected)"
                        :creator="itemSelected.creator"
                        :assigned="itemSelected.assigned"
                        :current-staff-id="currentStaffId" />
        </b-modal>

        <b-modal v-model="modal.active" size="lg" no-close-on-esc no-close-on-backdrop @hidden="resetModal">
          <div slot="modal-header" minimize class="app-modal-header">
            <span class="app-modal-header__icon"><AppIcon :name="modal.duplicate ? 'content_copy' : (modal.id ? 'edit' : 'add')" /></span>
            <h5 class="app-modal-header__title">{{ modalTitle }}</h5>
          </div>

          <div class="task-form">
            <section class="task-form__section">
              <b-row v-if="modal.id && modal.changeRelation">
                <b-col md="4">
                  <b-form-group label="Tipo">
                    <b-form-select v-model="modal.relationMode" :options="relationModeOptions" @change="onRelationModeChange" />
                  </b-form-group>
                </b-col>
              </b-row>
              <b-row>
                <b-col md="8">
                  <b-form-group label="Título">
                    <b-form-input v-model="form.title" maxlength="255" autocomplete="off" />
                  </b-form-group>
                </b-col>
                <b-col md="4">
                  <b-form-group label="Categoría *">
                    <v-select taggable push-tags :options="formCategoryOptions" v-model="form.category" placeholder="Seleccioná o creá una categoría" :select-on-tab="true" />
                  </b-form-group>
                </b-col>
              </b-row>

              <b-row>
                <b-col md="8" v-if="modal.relationMode == 'personal'">
                  <FindObject render="search"
                              type="staff"
                              tag="Asignado a"
                              description="Si se deja vacío, se asigna al creador"
                              @select-object="loadAssigned($event)"
                              :valueID="form.assigned_staff_id"
                              :key="'task-assigned-' + formKey" />
                </b-col>
                <b-col md="8" v-if="modal.relationMode == 'customer'">
                  <FindObject render="search"
                              type="customers"
                              @select-object="loadCustomer($event)"
                              :valueID="form.customer_id"
                              :key="'task-customer-' + formKey" />
                </b-col>
                <b-col md="8" v-if="modal.relationMode == 'supplier'">
                  <FindObject render="search"
                              type="suppliers"
                              @select-object="loadSupplier($event)"
                              :valueID="form.supplier_id"
                              :key="'task-supplier-' + formKey" />
                </b-col>
                <b-col md="8" v-if="modal.relationMode == 'project'">
                  <FindObject render="search"
                              type="project"
                              @select-object="loadProject($event)"
                              :valueID="form.project_id"
                              :key="'task-project-' + formKey" />
                </b-col>
                <b-col md="4">
                  <b-form-group label="Prioridad">
                    <b-dropdown
                      block
                      no-caret
                      boundary="window"
                      variant="light"
                      :toggle-class="['task-priority-toggle', 'task-priority-toggle--' + form.priority]"
                    >
                      <template #button-content>
                        <AppIcon :name="currentPriorityOption.icon" />
                        {{ currentPriorityOption.label }}
                        <AppIcon name="expand_more" class="task-priority-toggle__caret" />
                      </template>
                      <b-dropdown-item
                        v-for="option in priorityOptions"
                        :key="'priority-option-' + option.value"
                        @click="form.priority = option.value"
                      >
                        <span
                          class="task-priority-menu-item"
                          :class="['task-priority-menu-item--' + option.value, { 'task-priority-menu-item--active': form.priority == option.value }]"
                        >
                          <AppIcon :name="option.icon" />
                          {{ option.label }}
                          <AppIcon name="check" class="task-priority-menu-item__check" v-if="form.priority == option.value" />
                        </span>
                      </b-dropdown-item>
                    </b-dropdown>
                  </b-form-group>
                </b-col>
              </b-row>

              <b-row v-if="modal.relationMode != 'personal'">
                <b-col md="8">
                  <FindObject render="search"
                              type="staff"
                              tag="Asignado a"
                              description="Si se deja vacío, se asigna al creador"
                              @select-object="loadAssigned($event)"
                              :valueID="form.assigned_staff_id"
                              :key="'task-assigned-' + formKey" />
                </b-col>
              </b-row>
            </section>

            <section class="task-form__section">
              <RichEditorImages
                v-model="form.description"
                :images="form.images"
                @load-image="loadImages"
                type-image="Tasks"
                :max-image="5"
              >
                <template #after-editor>
                  <AppFileUpload
                    v-model="form.files"
                    help="Máximo 5 archivos de 10 MB cada uno"
                    :disabled="modal.saving"
                    @validation-error="$awn.warning($event)"
                  />
                </template>
              </RichEditorImages>
            </section>
          </div>

          <template v-slot:modal-footer>
            <b-button variant="outline-secondary" @click="cancelModal"><AppIcon name="x" class="mr-1" />Cancelar</b-button>
            <b-button variant="dark" :disabled="!form.title || modal.saving" @click="save">
              <b-spinner small v-if="modal.saving"></b-spinner>
              <template v-else><AppIcon name="save" class="mr-1" />Guardar</template>
            </b-button>
          </template>
        </b-modal>

        <b-modal
          v-model="projectHoursCompletion.active"
          centered
          size="md"
          :title="projectHoursModalTitle"
          :hide-header-close="projectHoursCompletion.saving"
          :no-close-on-backdrop="projectHoursCompletion.saving"
          :no-close-on-esc="projectHoursCompletion.saving"
          @shown="$refs.projectHoursInput && $refs.projectHoursInput.focus()"
          @hidden="resetProjectHoursCompletion"
        >
          <template #modal-header="{ close }">
            <div class="task-hours-modal__header">
              <span class="task-hours-modal__header-icon"><AppIcon name="schedule" /></span>
              <span><small>Registro de tiempo</small><strong>{{ projectHoursModalTitle }}</strong></span>
              <b-button variant="link" class="task-hours-modal__close" :disabled="projectHoursCompletion.saving" @click="close"><AppIcon name="close" label="Cerrar" /></b-button>
            </div>
          </template>
          <p class="task-hours-modal__intro">{{ projectHoursModalDescription }}</p>
          <b-row>
            <b-col cols="7">
              <b-form-group label="Fecha" label-for="task-project-hours-date">
                <b-form-input id="task-project-hours-date" v-model="projectHoursCompletion.date" type="date" :disabled="projectHoursCompletion.saving" />
              </b-form-group>
            </b-col>
            <b-col cols="5">
              <b-form-group label="Horas" label-for="task-project-hours" class="mb-0">
                <b-form-input
                  id="task-project-hours"
                  ref="projectHoursInput"
                  v-model.number="projectHoursCompletion.hours"
                  type="number"
                  :min="projectHoursMinimum"
                  step="0.01"
                  :disabled="projectHoursCompletion.saving"
                  @keyup.enter="saveProjectHours"
                />
              </b-form-group>
            </b-col>
          </b-row>
          <small class="form-text text-muted mt-2">Quedará vinculada a tu usuario dentro de esta tarea.</small>
          <b-form-group label="Detalle" label-for="task-project-hours-description" class="mt-3 mb-0">
            <b-form-textarea id="task-project-hours-description" v-model.trim="projectHoursCompletion.description" rows="3" maxlength="1000" :disabled="projectHoursCompletion.saving" placeholder="Describí el trabajo realizado" />
          </b-form-group>
          <template #modal-footer>
            <b-button variant="outline-secondary" :disabled="projectHoursCompletion.saving" @click="projectHoursCompletion.active = false">Cancelar</b-button>
            <b-button variant="success" :disabled="!canSubmitProjectHours || projectHoursCompletion.saving" @click="saveProjectHours">
              <b-spinner small v-if="projectHoursCompletion.saving" />
              <template v-else><AppIcon :name="projectHoursCompletion.mode == 'complete' ? 'check2-circle' : 'save'" class="mr-1" />{{ projectHoursCompletion.mode == 'complete' ? 'Completar' : (projectHoursCompletion.mode == 'edit' ? 'Guardar cambios' : 'Cargar horas') }}</template>
            </b-button>
          </template>
        </b-modal>
      </div>

      <b-card body-class="py-2" ref="taskFooterCard">
        <div class="list-view-footer">
          <b-button type="button" variant="outline-dark" size="sm" @click="$router.go(-1)">
            <AppIcon name="back" />
            Volver
          </b-button>
        </div>
      </b-card>
    </b-col>
  </b-row>
</template>

<script>
import moment from 'moment'
import Error from '@/handler/error'
import StorageLocal from '@/handler/storageLocal'
import Session from '@/handler/session'
import Profiles from '@/config/profiles'
import Modules from '@/config/modules'
import Helper from '@/handler/helper'
import TabHeaderActions from '@/components/inc/tabs/TabHeaderActions'
import FindObject from '@/components/inc/find/findObject'
import ChecklistViewer from '@/components/inc/checklist/ChecklistViewer'
import ChecklistUtils from '@/components/inc/checklist/checklistUtils'
import RichEditorImages from '@/components/inc/editor/RichEditorImages'
import AppFileUpload from '@/components/inc/file/AppFileUpload'
import TaskComments from '@/components/modules/users/messages/staff/TaskComments'
import messagesServiceAPI from '@/components/modules/users/messages/staff/services'
import Funciones from '@/handler/funciones'
import serviceAPI from './services'

export default {
  components: {
    TabHeaderActions,
    FindObject,
    ChecklistViewer,
    RichEditorImages,
    AppFileUpload,
    TaskComments,
  },
  data: () => {
    return {
      Modules,
      Profiles,
      access: {
        module_id: Modules.TAREAS,
        profile_id: Profiles.PERSONAL,
        view_reference: 'crudTasks',
        elements: {},
      },
      loading: true,
      taskLoadRequestId: 0,
      contextLoading: true,
      contextOptionsRequestId: 0,
      isMobileViewport: false,
      // Espacio visible de la pantalla debajo del encabezado (sin generar
      // scroll de página): es el alto mínimo del contenedor de tareas.
      viewportMinHeight: null,
      // Alto definitivo del workspace = max(pantalla, alto natural de la página
      // de tareas). Lo fija el contenedor de tareas; el panel de contexto lo
      // iguala (align-items:stretch) y su lista scrollea si excede.
      workspaceHeight: null,
      filtersOpen: false,
      contextPanelOpen: true,
      contextType: 'mine',
      contextSearch: '',
      items: [],
      options: {
        categories: [],
        staff: [],
        customers: [],
        suppliers: [],
        projects: [],
        context_counts: {
          customers: [],
          suppliers: [],
          projects: [],
          assigned: [],
          categories: [],
          mine: {
            pending: 0,
            completed: 0,
            archived: 0,
          },
        },
        category_groups: {
          personal: [],
          customers: [],
          suppliers: [],
          projects: [],
        },
        current_staff_id: null,
      },
      availableFormCategories: [],
      availableFilterCategories: [],
      formCategoryRequestId: 0,
      filterCategoryRequestId: 0,
      filters: {
        search: '',
        completionStatus: 'pending',
        archiveStatus: 'active',
        taskId: null,
        relationType: '',
        category: null,
        priority: '',
        customer: null,
        supplier: null,
        project: null,
        assigned: null,
      },
      currentPage: 1,
      perPage: 25,
      totalItems: 0,
      detailDrawer: false,
      itemSelected: null,
      showTaskComments: false,
      taskCommentsCount: null,
      checklistSaving: false,
      prioritySaving: false,
      movingTaskId: null,
      projectHoursCompletion: {
        active: false,
        saving: false,
        item: null,
        hours: null,
        mode: 'complete',
        editingHourId: null,
        date: '',
        description: '',
      },
      modal: {
        active: false,
        saving: false,
        duplicate: false,
        changeRelation: false,
        id: null,
        relationMode: 'personal',
        returnTarget: null,
      },
      formKey: 0,
      form: {
        title: '',
        description: '',
        category: null,
        priority: 'low',
        assigned: null,
        assigned_staff_id: 0,
        customer: null,
        customer_id: 0,
        supplier: null,
        supplier_id: 0,
        project: null,
        project_id: 0,
        images: [],
        files: [],
      },
    }
  },
  computed: {
    workspaceMinHeightStyle() {
      if (this.isMobileViewport) return {}
      var style = {}
      if (this.viewportMinHeight) style['--task-min-h'] = this.viewportMinHeight + 'px'
      if (this.workspaceHeight) style['--task-ws-h'] = this.workspaceHeight + 'px'
      return style
    },
    validProjectHours() {
      var hours = Number(this.projectHoursCompletion.hours)
      return Number.isFinite(hours) && hours > 0 && hours <= 24 && Math.abs(hours - Math.round(hours * 100) / 100) < 0.00001
    },
    canSubmitProjectHours() {
      if (this.validProjectHours) return true
      var hours = Number(this.projectHoursCompletion.hours)
      return this.projectHoursCompletion.mode == 'complete' && this.projectHoursHasRecords && hours === 0
    },
    projectHoursHasRecords() {
      var item = this.projectHoursCompletion.item
      return !!(item && Number(item.project_hours_count || 0) > 0)
    },
    projectHoursMinimum() {
      return this.projectHoursCompletion.mode == 'complete' && this.projectHoursHasRecords ? 0 : 0.01
    },
    projectHoursModalTitle() {
      if (this.projectHoursCompletion.mode == 'edit') return 'Editar horas de tarea'
      return this.projectHoursCompletion.mode == 'complete' ? 'Completar tarea de proyecto' : 'Cargar horas de tarea'
    },
    projectHoursModalDescription() {
      var label = this.taskLabel(this.projectHoursCompletion.item)
      if (this.projectHoursCompletion.mode == 'edit') return 'Actualizá esta imputación de ' + label + '.'
      if (this.projectHoursCompletion.mode == 'add') return 'Registrá una nueva iteración de horas para ' + label + '.'
      if (this.projectHoursHasRecords) return 'La tarea ya tiene horas registradas. Indicá 0 para completarla sin agregar otro parte, o una cantidad para sumar una última imputación.'
      return 'Para completar ' + label + ', indicá las horas realizadas.'
    },
    categoryOptions() {
      return (this.options.categories || []).map(item => {
        return typeof item === 'string' ? item : item.label
      }).filter(item => item)
    },
    formCategoryOptions() {
      return this.availableFormCategories
    },
    staffOptions() {
      return (this.options.staff || []).map(item => ({ code: item.id, label: item.name, image: item.image }))
    },
    customerOptions() {
      return (this.options.customers || []).map(item => ({
        code: item.id,
        label: item.name,
        name: item.name,
        image: item.image,
      }))
    },
    supplierOptions() {
      return (this.options.suppliers || []).map(item => ({
        code: item.id,
        label: item.name,
        name: item.name,
        image: item.image,
      }))
    },
    projectOptions() {
      return (this.options.projects || []).map(item => {
        return {
          code: item.id,
          label: item.name,
          secondaryMeta: item.customer && item.customer.name ? item.customer.name : '',
        }
      })
    },
    contextCounts() {
      return this.options.context_counts || {
        customers: [],
        suppliers: [],
        projects: [],
        assigned: [],
        categories: [],
        mine: {},
      }
    },
    hasRelationContext() {
      return !!(this.filters.customer || this.filters.supplier || this.filters.project)
    },
    tableFields() {
      var fields = [
        { key: 'title', label: 'Tarea', sortable: false },
        { key: 'priority', label: 'Prioridad', class: 'align-middle ' + this.alignClass('text-center task-table-priority-col') },
        { key: 'checklist_progress', label: 'Seguimiento', class: 'align-middle ' + this.alignClass('text-center task-table-progress-col') },
        { key: 'status', label: 'Estado', class: 'align-middle ' + this.alignClass('text-center') },
        { key: 'category', label: 'Categoría', class: 'align-middle ' + this.alignClass('text-center task-table-category-col') },
      ]

      if (!this.hasRelationContext) {
        fields.push({ key: 'relation', label: 'Contexto' })
      }

      fields.push(
        { key: 'people', label: 'Personas' },
        { key: 'created_at', label: 'Creada' },
        {
          key: 'actions',
          label: this.isMobileViewport ? 'Acción' : '',
          class: 'align-middle ' + this.alignClass('text-right'),
        }
      )

      return fields
    },
    contextTypes() {
      var types = [
        { value: 'mine', label: 'Mis tareas', icon: 'person' },
      ]
      if (this.canCreateCustomerTask) types.push({ value: 'customers', label: 'Clientes', icon: 'business' })
      if (this.canCreateSupplierTask) types.push({ value: 'suppliers', label: 'Proveedores', icon: 'local_shipping' })
      if (this.canCreateProjectTask) types.push({ value: 'projects', label: 'Proyectos', icon: 'folder' })
      return types
    },
    contextNodes() {
      if (this.contextType == 'customers') return this.contextNodesFromOptions('customers', this.customerOptions, this.contextCountLabel, 'business')
      if (this.contextType == 'suppliers') return this.contextNodesFromOptions('suppliers', this.supplierOptions, this.contextCountLabel, 'local_shipping')
      if (this.contextType == 'projects') return this.contextNodesFromOptions('projects', this.projectOptions, this.contextCountLabel, 'folder')

      var mine = this.contextCounts.mine || {}
      var nodes = [
        { id: 'personal', label: 'Personal', meta: this.contextCountLabel, icon: 'person', count: Number(mine.personal || 0), urgentCount: Number(mine.personal_urgent || 0), variant: 'warning' },
      ]
      if (this.canCreateCustomerTask) nodes.push({ id: 'customer', label: 'Clientes', meta: this.contextCountLabel, icon: 'business', count: Number(mine.customers || 0), urgentCount: Number(mine.customers_urgent || 0), variant: 'warning' })
      if (this.canCreateSupplierTask) nodes.push({ id: 'supplier', label: 'Proveedores', meta: this.contextCountLabel, icon: 'local_shipping', count: Number(mine.suppliers || 0), urgentCount: Number(mine.suppliers_urgent || 0), variant: 'warning' })
      if (this.canCreateProjectTask) nodes.push({ id: 'project', label: 'Proyectos', meta: this.contextCountLabel, icon: 'folder', count: Number(mine.projects || 0), urgentCount: Number(mine.projects_urgent || 0), variant: 'warning' })
      return nodes
    },
    contextCountLabel() {
      if (this.filters.archiveStatus == 'archived') return 'Tareas archivadas'
      return this.filters.completionStatus == 'completed' ? 'Tareas completadas' : 'Tareas pendientes'
    },
    filteredContextNodes() {
      if (!this.contextSearch) return this.contextNodes
      var search = this.contextSearch.toLowerCase()
      return this.contextNodes.filter(item => item.label.toLowerCase().indexOf(search) !== -1)
    },
    selectedContextType() {
      return this.contextTypes.find(item => item.value == this.contextType) || this.contextTypes[0]
    },
    completionStatusOptions() {
      return [
        { value: 'pending', text: 'Pendientes' },
        { value: 'completed', text: 'Completadas' },
      ]
    },
    archiveStatusOptions() {
      return [
        { value: 'active', text: 'Activas' },
        { value: 'archived', text: 'Archivadas' },
      ]
    },
    relationTypeOptions() {
      var options = [
        { value: '', text: 'Todos' },
        { value: 'personal', text: 'Personal' },
      ]
      if (this.canCreateCustomerTask) options.push({ value: 'customer', text: 'Clientes' })
      if (this.canCreateSupplierTask) options.push({ value: 'supplier', text: 'Proveedores' })
      if (this.canCreateProjectTask) options.push({ value: 'project', text: 'Proyectos' })
      return options
    },
    priorityFilterOptions() {
      return [{ value: '', text: 'Todas' }].concat(this.priorityOptions.map(item => ({ value: item.value, text: item.label })))
    },
    relationModeOptions() {
      var options = [{ value: 'personal', text: 'Personal' }]
      if (this.canCreateCustomerTask) options.push({ value: 'customer', text: 'Cliente' })
      if (this.canCreateSupplierTask) options.push({ value: 'supplier', text: 'Proveedor' })
      if (this.canCreateProjectTask) options.push({ value: 'project', text: 'Proyecto' })
      return options
    },
    filterCategoryOptions() {
      return [{ value: null, text: 'Todas' }].concat(this.availableFilterCategories.map(category => ({ value: category, text: category })))
    },
    canFilterByAssigned() {
      return ['customer', 'supplier', 'project'].indexOf(this.filters.relationType) !== -1
    },
    canReorderTasks() {
      // El endpoint determina el scope real de cada tarea (prioridad +
      // contexto); no depende del filtro superior que esté viendo el
      // usuario. Por eso "Todos" también puede ofrecer reordenamiento.
      if (this.filters.completionStatus != 'pending' || this.filters.archiveStatus != 'active') return false
      return this.items.length > 0
    },
    currentStaffId() {
      return this.options.current_staff_id || null
    },
    assignedFilterOptions() {
      return [{ value: null, text: 'Todos' }].concat(this.staffOptions.map(staff => ({ value: staff.code, text: staff.label })))
    },
    priorityOptions() {
      return [
        { value: 'urgent', label: 'Urgente', icon: 'priority_high' },
        { value: 'high', label: 'Alta', icon: 'arrow_upward' },
        { value: 'medium', label: 'Media', icon: 'horizontal_rule' },
        { value: 'low', label: 'Baja', icon: 'arrow_downward' },
      ]
    },
    currentPriorityOption() {
      return this.priorityOptionFor(this.form.priority)
    },
    // Navegación del sidebar de detalle (mismo patrón que
    // reminders/staff/crudReminders.vue -- currentReminderIndex/
    // canGoPrevReminder/canGoNextReminder + .list-view-drawer__nav):
    // se mueve sobre `items`, la lista ya filtrada/ordenada, no solo la
    // página actual, para que "siguiente" avance en el mismo orden que ve
    // el usuario en la tabla.
    currentTaskIndex() {
      if (!this.itemSelected) return -1
      return this.items.findIndex(item => item.id == this.itemSelected.id)
    },
    canGoPrevTask() {
      return this.currentTaskIndex > 0
    },
    canGoNextTask() {
      return this.currentTaskIndex !== -1 && this.currentTaskIndex < this.items.length - 1
    },
    canCreateCustomerTask() {
      return this.canAccessRelatedModule(Modules.CLIENTES, 'crud')
    },
    canCreateSupplierTask() {
      return this.canAccessRelatedModule(Modules.PROVEEDORES, 'crud')
    },
    canCreateProjectTask() {
      return this.canAccessRelatedModule(Modules.PROYECTOS, 'crudProject')
    },
    canAccessReminders() {
      var modules = Session.getSession().auth && Session.getSession().auth.user.permissions.modules
      var remindersModuleActive = Array.isArray(modules) && modules.some(module => module.id == Modules.RECORDATORIOS && module.active)
      return remindersModuleActive && Helper.showAccessView({ module_id: Modules.RECORDATORIOS, profile_id: Profiles.PERSONAL }, 'crudReminders')
    },
    detailChecklistStatus() {
      return this.itemSelected ? (this.itemSelected.checklist_status || ChecklistUtils.getStatus(this.itemSelected.description)) : null
    },
    hasFilters() {
      return !!(this.filters.search || this.filters.category || this.filters.priority || this.filters.relationType || this.filters.customer || this.filters.supplier || this.filters.project || this.filters.assigned || this.filters.completionStatus !== 'pending' || this.filters.archiveStatus !== 'active')
    },
    modalTitle() {
      if (this.modal.duplicate) return 'Duplicar tarea'
      if (this.modal.changeRelation) return 'Cambiar tipo de tarea'
      return this.modal.id ? 'Editar tarea' : 'Agregar tarea'
    },
  },
  watch: {
    itemSelected(item) {
      this.loadTaskCommentsCount(item)
    },
    '$route.params.taskId'() {
      this.applyTaskRouteFilter()
      this.load()
    },
    '$route.query.context'() {
      this.applyTaskRouteContext()
      this.load()
    },
    '$route.query.relation_type'() {
      this.applyTaskRouteContext()
      this.load()
    },
    '$route.query.my_space_filter'() {
      this.applyTaskRouteContext()
      this.load()
    },
    '$route.query.widget_task_filter'() {
      this.applyTaskRouteWidgetFilters()
      this.load()
    },
    filtersOpen(value) {
      StorageLocal.setValue('filters_open_crud_tasks', value ? 'true' : 'false')
      // El alto del encabezado cambia al abrir/cerrar filtros -> recalcular.
      this.$nextTick(() => window.setTimeout(this.measureViewportMinHeight, 350))
    },
    filters: {
      deep: true,
      handler() {
        this.currentPage = 1
      },
    },
    currentPage(value, oldValue) {
      if (value !== oldValue) this.load()
    },
  },
  created() {
    Helper.hasAccessView(this.access)
  },
    mounted() {
      this.filtersOpen = StorageLocal.getValue('filters_open_crud_tasks') == 'true'
      this.applyTaskRouteFilter()
      this.applyTaskRouteContext()
      this.applyTaskRouteWidgetFilters()
      this.load()
    if (this.$route.query.new == '1') {
      var createMode = this.$route.query.mode || 'personal'
      var createRelationId = this.$route.query.relation_id || null
      var createReturnTarget = this.taskCreateReturnTarget()
      this.$nextTick(() => this.openCreate(createMode, createRelationId, createReturnTarget))
      // Limpia el query apenas se consume -- si no, la pestaña queda con
      // ?new=1 pegado para siempre y volver a ella (activated, keep-alive)
      // reabriría el alta de nuevo aunque el usuario solo quiera ver el
      // listado.
      this.$router.replace({ path: this.$route.path, query: {} }).catch(() => {})
    }
    this._start = true

    this.mobileMql = window.matchMedia('(max-width: 767.98px)')
    this.isMobileViewport = this.mobileMql.matches
    this.mobileMql.addEventListener('change', this.updateMobileViewport)

    window.addEventListener('resize', this.onViewportResize)
    this.measureViewportMinHeight()
  },
  beforeDestroy() {
    if (this.mobileMql) this.mobileMql.removeEventListener('change', this.updateMobileViewport)
    window.removeEventListener('resize', this.onViewportResize)
    if (this._resizeRaf) window.cancelAnimationFrame(this._resizeRaf)
  },
  // Esta vista queda con keep-alive (ver DefaultTabsLayout.vue) --
  // activated() sirve para repetir acá el mismo chequeo de ?new=1 que
  // "mounted" no vuelve a correr al volver a una pestaña ya montada (ej.
  // la acción "Cargar tarea..." de la paleta de comandos).
  activated() {
    if (this._start) {
      this._start = false
      return
    }

    this.applyTaskRouteContext()
    this.applyTaskRouteWidgetFilters()
    this.load()
    this.measureViewportMinHeight()

    if (this.$route.query.new == '1') {
      var createMode = this.$route.query.mode || 'personal'
      var createRelationId = this.$route.query.relation_id || null
      var createReturnTarget = this.taskCreateReturnTarget()
      this.$nextTick(() => this.openCreate(createMode, createRelationId, createReturnTarget))
      this.$router.replace({ path: this.$route.path, query: {} }).catch(() => {})
    }
  },
  methods: {
    updateMobileViewport(e) {
      this.isMobileViewport = e.matches
      this.measureViewportMinHeight()
    },
    onViewportResize() {
      if (this._resizeRaf) return
      this._resizeRaf = window.requestAnimationFrame(() => {
        this._resizeRaf = null
        this.measureViewportMinHeight()
      })
    },
    measureViewportMinHeight() {
      this.$nextTick(() => {
        var ws = this.$refs.taskWorkspace
        if (!ws || this.isMobileViewport) {
          this.viewportMinHeight = null
          this.workspaceHeight = null
          return
        }
        var top = ws.getBoundingClientRect().top
        var footerCard = this.$refs.taskFooterCard && this.$refs.taskFooterCard.$el
        // Reserva: la tarjeta "Volver" que va debajo + un margen inferior
        // real. Evita que, cuando hay pocas tareas, el último píxel del
        // workspace fuerce scroll de página por bordes, márgenes o redondeo.
        var reserved = (footerCard ? footerCard.offsetHeight : 0) + 36
        var available = window.innerHeight - top - reserved
        // Cota superior: nunca más que el alto de la ventana (protege el caso
        // de recalcular con la página scrolleada, donde `top` es negativo).
        var minHeight = Math.max(320, Math.min(window.innerHeight, Math.round(available)))
        this.viewportMinHeight = minHeight

        // Alto natural del contenido de tareas: la <table> renderiza siempre
        // todas las filas de la página (hasta perPage, 25 por defecto) + el
        // pie + el margen superior de su tarjeta. Medir la tabla directamente
        // evita que el wrapper responsive convierta las filas en scroll
        // vertical cuando el workspace todavía conserva una altura anterior.
        // En BootstrapVue la clase del <b-table> puede quedar sobre el
        // wrapper .table-responsive. Ese wrapper se estira con flex y no
        // representa el alto de las filas; tomar la tabla interna evita que
        // el alto mínimo anterior se retroalimente indefinidamente.
        var table = ws.querySelector('.task-list-table table') ||
          ws.querySelector('table.task-list-table')
        var footerBar = ws.querySelector('.task-table-footer')
        var tasksContent = (table ? table.offsetHeight : 0) +
          (footerBar ? footerBar.offsetHeight : 0) + 24
        // El contenedor de tareas manda: su alto define el del workspace, y con
        // align-items:stretch el panel de contexto queda exactamente igual.
        this.workspaceHeight = Math.max(minHeight, Math.round(tasksContent))
      })
    },
    applyTaskRouteFilter() {
      var taskId = Number(this.$route.params.taskId || 0)
      this.filters.taskId = taskId > 0 ? taskId : null
    },
    applyTaskRouteContext() {
      if (this.$route.query.context != 'mine') return

      this.contextType = 'mine'
      if (this.$route.query.relation_type == 'personal') {
        this.filtersOpen = true
        this.applyContextFilters('personal', null, true)
      }
    },
    applyTaskRouteWidgetFilters() {
      if (!this.$route.query.widget_task_filter) return

      var relationType = this.$route.query.relation_type
      if (['customer', 'supplier', 'project'].indexOf(relationType) === -1) return

      var relationId = this.$route.query.relation_id
      this.filtersOpen = true
      this.contextType = relationType + 's'
      this.applyContextFilters(relationType, null, true)
      if (relationType == 'customer') this.filters.customer = { code: relationId }
      if (relationType == 'supplier') this.filters.supplier = { code: relationId }
      if (relationType == 'project') this.filters.project = { code: relationId }
      this.filters.assigned = this.$route.query.assigned_staff_id || null
      this.filters.priority = this.$route.query.priority || ''
    },
    canAccessRelatedModule(moduleId, reference) {
      var modules = Session.getSession().auth && Session.getSession().auth.user.permissions.modules
      return Array.isArray(modules) && modules.some(module => module.id == moduleId && module.active) &&
        Helper.showAccessView({ module_id: moduleId, profile_id: Profiles.PERSONAL }, reference)
    },
    alignClass(desktopClass) {
      return this.isMobileViewport ? 'text-left' : desktopClass
    },
    loadOptions() {
      var requestId = ++this.contextOptionsRequestId
      this.contextLoading = true
      serviceAPI.obtenerOpciones(this.contextOptionsFilters())
        .then(response => {
          if (requestId != this.contextOptionsRequestId) return
          this.options = response.data || this.options
          this.contextLoading = false
        })
        .catch(() => {
          if (requestId != this.contextOptionsRequestId) return
          this.contextLoading = false
        })
    },
    loadFormCategoryOptions() {
      var relationType = this.modal.relationMode
      var relationId = relationType == 'customer'
        ? this.form.customer_id
        : (relationType == 'supplier' ? this.form.supplier_id : (relationType == 'project' ? this.form.project_id : null))
      var requestId = ++this.formCategoryRequestId

      // Para contextos relacionados no se muestran sugerencias hasta elegir
      // la entidad: las categorías pertenecen al cliente/proveedor/proyecto.
      if (relationType != 'personal' && !relationId) {
        this.availableFormCategories = []
        return
      }

      serviceAPI.obtenerOpciones({
        relation_type: relationType,
        [relationType + '_id']: relationId || '',
        archive_status: 'active',
      }).then(response => {
        if (requestId != this.formCategoryRequestId) return
        this.availableFormCategories = Array.isArray(response.data && response.data.available_categories)
          ? response.data.available_categories
          : []
      }).catch(() => {})
    },
    loadFilterCategoryOptions() {
      var requestId = ++this.filterCategoryRequestId
      if (!this.filters.relationType) {
        this.availableFilterCategories = []
        return
      }

      serviceAPI.obtenerOpciones({
        relation_type: this.filters.relationType,
        archive_status: this.filters.archiveStatus,
        task_id: this.filters.taskId,
      }).then(response => {
        if (requestId != this.filterCategoryRequestId) return
        this.availableFilterCategories = Array.isArray(response.data && response.data.available_categories)
          ? response.data.available_categories
          : []
      }).catch(() => {})
    },
    load() {
      var requestId = ++this.taskLoadRequestId
      this.loading = true
      this.loadOptions()
      serviceAPI.obtenerTareas(Object.assign({}, this.requestFilters(), {
        page: this.currentPage,
        per_page: this.perPage,
      }))
        .then(response => {
          if (requestId != this.taskLoadRequestId) return
          var payload = response.data || {}
          if (Array.isArray(payload)) {
            this.items = payload
            this.totalItems = payload.length
          } else {
            this.items = Array.isArray(payload.data) ? payload.data : []
            this.totalItems = Number(payload.total || 0)
            if (payload.current_page && payload.current_page !== this.currentPage) {
              this.currentPage = Number(payload.current_page)
            }
          }
          this.openTaskFromRoute()
          this.loading = false
          this.measureViewportMinHeight()
        })
        .catch(error => {
          if (requestId != this.taskLoadRequestId) return
          this.loading = false
          this.$awn.alert(Error.showError(error))
        })
    },
    openTaskFromRoute() {
      var taskId = Number(this.filters.taskId || 0)
      if (!taskId) return

      var task = this.items.find(item => Number(item.id) === taskId)
      if (task) this.openDetail(task)
    },
    // La URL /list/:taskId abre el detalle como punto de entrada. Al cerrar
    // el sidebar se consume esa selección para que las siguientes cargas de
    // tabla (búsqueda, filtros, etc.) no vuelvan a abrirlo.
    closeDetailFromRoute() {
      this.itemSelected = null
      if (!this.$route.params.taskId) return

      this.$router.replace({
        name: 'TasksStaffCrud',
        query: this.$route.query,
      }).catch(() => {})
    },
    contextNodesFromOptions(group, options, meta, icon) {
      var counts = this.countsMap(group)

      return options.map(item => {
        var countData = counts[item.code] || {}
        var count = Number(countData.count || 0)
        return {
          id: item.code,
          label: item.label,
          meta: meta,
          secondaryMeta: item.secondaryMeta || '',
          icon: icon,
          image: item.image,
          avatar: group == 'customers' || group == 'suppliers' || group == 'assigned',
          count: count,
          urgentCount: Number(countData.urgent || 0),
        }
      }).filter(item => item.count > 0)
        .sort((a, b) => b.urgentCount - a.urgentCount || b.count - a.count || a.label.localeCompare(b.label))
    },
    countsMap(group) {
      return (this.contextCounts[group] || []).reduce((map, item) => {
        map[item.id] = {
          count: Number(item.task_count || item.pending_count || 0),
          urgent: Number(item.urgent_count || 0),
        }
        return map
      }, {})
    },
    countBy(key, value) {
      return this.items.filter(item => item[key] == value).length
    },
    requestFilters() {
      return {
        completion_status: this.filters.completionStatus,
        archive_status: this.filters.archiveStatus,
        relation_type: this.filters.relationType,
        search: this.filters.search,
        category: this.filters.category,
        priority: this.filters.priority,
        task_id: this.filters.taskId,
        customer_id: this.optionId(this.filters.customer),
        supplier_id: this.optionId(this.filters.supplier),
        project_id: this.optionId(this.filters.project),
        assigned_staff_id: this.optionId(this.filters.assigned),
      }
    },
    contextOptionsFilters() {
      return {
        completion_status: this.filters.completionStatus,
        archive_status: this.filters.archiveStatus,
        search: this.filters.search,
        category: this.filters.category,
        priority: this.filters.priority,
        assigned_staff_id: this.optionId(this.filters.assigned),
      }
    },
    clearFilters() {
      this.filters.search = ''
      this.filters.completionStatus = 'pending'
      this.filters.archiveStatus = 'active'
      this.filters.taskId = null
      this.filters.relationType = ''
      this.filters.assigned = null
      this.filters.category = null
      this.filters.priority = ''
      this.filters.customer = null
      this.filters.supplier = null
      this.filters.project = null
      this.load()
    },
    onRelationTypeChange() {
      this.filters.category = null
      this.filters.assigned = null
      this.filters.customer = null
      this.filters.supplier = null
      this.filters.project = null
      this.loadFilterCategoryOptions()
    },
    onArchiveStatusChange() {
      this.filters.category = null
      this.loadFilterCategoryOptions()
    },
    selectContextType(type) {
      this.contextType = type
      this.contextSearch = ''
    },
    selectContextNode(node) {
      this.applyContextFilters(node.id == 'customer' || this.contextType == 'customers' ? 'customer' :
        (node.id == 'supplier' || this.contextType == 'suppliers' ? 'supplier' :
          (node.id == 'project' || this.contextType == 'projects' ? 'project' : 'personal')),
      this.contextType == 'mine' && node.id != 'personal' ? this.currentStaffId : undefined)

      if (this.contextType == 'mine') {
        if (node.id == 'personal') this.filters.assigned = null
      } else if (this.contextType == 'customers') {
        this.filters.customer = this.findOption(this.customerOptions, node.id)
      } else if (this.contextType == 'suppliers') {
        this.filters.supplier = this.findOption(this.supplierOptions, node.id)
      } else if (this.contextType == 'projects') {
        this.filters.project = this.findOption(this.projectOptions, node.id)
      }
      this.load()
    },
    // `undefined` significa conservar el responsable filtrado al navegar
    // entre entidades del mismo contexto. `null` se usa explícitamente
    // cuando corresponde limpiar el filtro (por ejemplo, Personal).
    applyContextFilters(relationType, assigned = undefined, resetTopFilters = false) {
      if (resetTopFilters) {
        this.filters.search = ''
        this.filters.completionStatus = 'pending'
        this.filters.archiveStatus = 'active'
        this.filters.category = null
        this.filters.priority = ''
      }
      this.filters.relationType = relationType
      this.filters.customer = null
      this.filters.supplier = null
      this.filters.project = null
      if (assigned !== undefined) this.filters.assigned = assigned
      this.loadFilterCategoryOptions()
    },
    taskCreateReturnTarget() {
      var relationType = this.$route.query.return_to_relation
      var relationId = Number(this.$route.query.return_relation_id || 0)
      return ['customer', 'supplier', 'project'].indexOf(relationType) !== -1 && relationId
        ? { type: relationType, id: relationId }
        : null
    },
    returnToRelatedDetail(target) {
      if (!target) return
      var route = target.type == 'customer'
        ? { name: 'CustomerViewStaff', params: { customersID: String(target.id) }, query: { tab: 'tasks' } }
        : target.type == 'supplier'
          ? { name: 'SuppliersViewStaff', params: { suppliersID: String(target.id) }, query: { tab: 'tasks' } }
          : { name: 'ProjectsStaffCrud', query: { project_id: String(target.id), project_search: String(Date.now()) } }
      this.$router.push(route).catch(() => {})
    },
    openCreate(relationMode = 'personal', relationId = null, returnTarget = null) {
      if (relationMode == 'customer' && !this.canCreateCustomerTask) relationMode = 'personal'
      if (relationMode == 'supplier' && !this.canCreateSupplierTask) relationMode = 'personal'
      if (relationMode == 'project' && !this.canCreateProjectTask) relationMode = 'personal'

      this.resetModal()
      this.modal.returnTarget = returnTarget
      this.modal.relationMode = relationMode
      if (relationMode == 'customer') this.form.customer_id = Number(relationId) || this.optionId(this.filters.customer) || 0
      if (relationMode == 'supplier') this.form.supplier_id = Number(relationId) || this.optionId(this.filters.supplier) || 0
      if (relationMode == 'project') this.form.project_id = Number(relationId) || this.optionId(this.filters.project) || 0
      this.modal.active = true
      this.loadFormCategoryOptions()
    },
    canCreateTaskReminder(item) {
      return this.canAccessReminders && this.canEditTask(item) && !this.taskReminder(item)
    },
    canDeleteTaskReminder(item) {
      var reminder = this.taskReminder(item)
      return this.canAccessReminders && this.canEditTask(item) && reminder && reminder.staff_id == this.currentStaffId
    },
    createReminder(item) {
      if (!this.canCreateTaskReminder(item)) return
      this.$router.push({
        name: 'RemindersStaffCrud',
        query: { new: '1', task_id: item.id, title: this.taskLabel(item), description: this.taskReminderDescription(item), return_to_task: '1' },
      })
    },
    removeTaskReminder(item) {
      var reminder = this.taskReminder(item)
      if (!reminder || !this.canDeleteTaskReminder(item)) return

      this.confirmDialog('¿Quitar el recordatorio vinculado a la tarea "' + this.taskLabel(item) + '"?', {
        title: 'Quitar recordatorio',
        icon: 'notifications_off',
        okTitle: 'QUITAR',
      }).then(confirmed => {
        if (!confirmed) return
        serviceAPI.eliminarRecordatorio(item.id)
          .then(() => {
            this.$set(item, 'reminder', null)
            if (this.itemSelected && this.itemSelected.id == item.id) this.$set(this.itemSelected, 'reminder', null)
            this.$awn.success('Recordatorio quitado')
          })
          .catch(error => this.$awn.alert(Error.showError(error)))
      })
    },
    openEdit(item, changeRelation = false) {
      this.modal.id = item.id
      this.modal.duplicate = false
      this.modal.changeRelation = changeRelation
      this.fillForm(item)
      this.modal.relationMode = this.detectRelationMode(item)
      this.modal.active = true
      this.loadFormCategoryOptions()
    },
    openChangeRelation(item) {
      this.openEdit(item, true)
    },
    openDuplicate(item) {
      this.modal.id = null
      this.modal.duplicate = true
      this.modal.changeRelation = false
      this.fillForm(item)
      this.modal.relationMode = this.detectRelationMode(item)
      this.modal.active = true
      this.loadFormCategoryOptions()
    },
    onRelationModeChange() {
      this.formKey++
      this.form.customer = null
      this.form.customer_id = 0
      this.form.supplier = null
      this.form.supplier_id = 0
      this.form.project = null
      this.form.project_id = 0
      this.loadFormCategoryOptions()
    },
    fillForm(item) {
      this.formKey++
      this.form.title = this.taskTitle(item)
      this.form.description = item.description || ''
      this.form.category = item.category || null
      this.form.priority = item.priority || 'low'
      this.form.assigned = item.assigned || null
      this.form.assigned_staff_id = item.assigned_staff_id || 0
      this.form.customer = item.customer || null
      this.form.customer_id = item.customer_id || 0
      this.form.supplier = item.supplier || null
      this.form.supplier_id = item.supplier_id || 0
      this.form.project = item.project || null
      this.form.project_id = item.project_id || 0
      this.form.images = this.taskImages(item).map(image => {
        return { path: image.url || image.path }
      }).filter(image => image.path)
      this.form.files = this.taskFiles(item).map(file => {
        return { name: file.name, path: file.path, url: file.url, size: file.size, mime: file.mime }
      }).filter(file => file.path)
    },
    loadAssigned(object) {
      this.form.assigned = object || null
      this.form.assigned_staff_id = object ? object.id : 0
    },
    loadCustomer(object) {
      this.form.customer = object || null
      this.form.customer_id = object ? object.id : 0
      this.loadFormCategoryOptions()
    },
    loadSupplier(object) {
      this.form.supplier = object || null
      this.form.supplier_id = object ? object.id : 0
      this.loadFormCategoryOptions()
    },
    loadProject(object) {
      this.form.project = object || null
      this.form.project_id = object ? object.id : 0
      this.loadFormCategoryOptions()
    },
    save() {
      var title = (this.form.title || '').trim()
      if (!title || this.modal.saving) return
      var category = (this.form.category || '').trim()
      if (!category) {
        this.$awn.alert('La categoría es obligatoria')
        return
      }
      if (this.modal.relationMode == 'customer' && !this.form.customer_id) {
        this.$awn.alert('Seleccioná el cliente para cambiar el tipo de tarea')
        return
      }
      if (this.modal.relationMode == 'supplier' && !this.form.supplier_id) {
        this.$awn.alert('Seleccioná el proveedor para cambiar el tipo de tarea')
        return
      }
      if (this.modal.relationMode == 'project' && !this.form.project_id) {
        this.$awn.alert('Seleccioná el proyecto para cambiar el tipo de tarea')
        return
      }

      this.modal.saving = true
      var data = {
        title: title,
        description: this.form.description,
        category: category,
        priority: this.form.priority || 'low',
        assigned_staff_id: this.form.assigned_staff_id || '',
        customer_id: this.modal.relationMode == 'customer' ? (this.form.customer_id || '') : '',
        supplier_id: this.modal.relationMode == 'supplier' ? (this.form.supplier_id || '') : '',
        project_id: this.modal.relationMode == 'project' ? (this.form.project_id || '') : '',
      }
      var request = this.modal.id
        ? serviceAPI.editarTarea(this.modal.id, data, this.form.images, this.form.files)
        : serviceAPI.agregarTarea(data, this.form.images, this.form.files)

      request
        .then(response => {
          var returnTarget = this.modal.returnTarget
          this.upsertItem(response.data)
          this.modal.saving = false
          this.modal.active = false
          if (returnTarget) this.$nextTick(() => this.returnToRelatedDetail(returnTarget))
        })
        .catch(error => {
          this.modal.saving = false
          this.$awn.alert(Error.showError(error))
        })
    },
    toggleComplete(item) {
      if (!this.canCompleteTask(item)) {
        this.$awn.alert('Solo el usuario asignado puede completar o volver una tarea a pendiente')
        return
      }
      var completing = !item.done
      if (completing && item.project_id && !Number(item.project_hours_count || 0)) {
        this.openProjectHoursCompletion(item)
        return
      }

      var message = completing
        ? '¿Marcar la tarea "' + this.taskLabel(item) + '" como completada?'
        : '¿Volver la tarea "' + this.taskLabel(item) + '" a pendiente?'
      if (!completing && item.project_id && Number(item.project_hours_count || 0) > 0) {
        message = 'Las horas ya registradas se conservarán. ¿Volver la tarea "' + this.taskLabel(item) + '" a pendiente?'
      }
      this.confirmDialog(
        message,
        {
          title: completing ? 'Completar tarea' : 'Reabrir tarea',
          icon: completing ? 'check2-circle' : 'undo',
          okTitle: completing ? 'COMPLETAR' : 'REABRIR',
          success: completing,
        }
      )
        .then(confirmed => {
          if (!confirmed) return
          serviceAPI.completarTarea(item.id)
            .then(response => {
              this.upsertItem(response.data)
            })
            .catch(error => {
              var message = Error.showError(error)
              this.$awn[message.indexOf('checklist') !== -1 ? 'warning' : 'alert'](message)
            })
        })
        .catch(() => {})
    },
    toggleStatusManagement(item) {
      var willManageStatus = !this.managesStatus(item)
      var message = willManageStatus
        ? 'La tarea volverá a permitir completarse, reabrirse y archivarse. ¿Continuar?'
        : 'La tarea quedará siempre activa: no se podrá completar, reabrir ni archivar. El checklist seguirá disponible para usarlo de forma repetitiva. ¿Continuar?'
      this.confirmDialog(message, {
        title: willManageStatus ? 'Gestionar estados' : 'Tarea permanente',
        icon: willManageStatus ? 'check_circle' : 'repeat',
        okTitle: willManageStatus ? 'HABILITAR' : 'HACER PERMANENTE',
      }).then(confirmed => {
        if (!confirmed) return
        serviceAPI.editarTarea(item.id, { manage_status: willManageStatus })
          .then(response => this.upsertItem(response.data))
          .catch(error => this.$awn.alert(Error.showError(error)))
      }).catch(() => {})
    },
    openProjectHoursCompletion(item, mode = 'complete') {
      this.projectHoursCompletion.item = item
      this.projectHoursCompletion.mode = mode
      this.projectHoursCompletion.hours = mode == 'complete' && Number(item.project_hours_count || 0) > 0 ? 0 : null
      this.projectHoursCompletion.editingHourId = null
      this.projectHoursCompletion.date = moment().format('YYYY-MM-DD')
      this.projectHoursCompletion.description = ''
      this.projectHoursCompletion.active = true
    },
    openEditProjectHours(item, hour) {
      this.projectHoursCompletion.item = item
      this.projectHoursCompletion.mode = 'edit'
      this.projectHoursCompletion.editingHourId = hour.id
      this.projectHoursCompletion.hours = Number(hour.hours)
      this.projectHoursCompletion.date = hour.date
      this.projectHoursCompletion.description = hour.description || ''
      this.projectHoursCompletion.active = true
    },
    resetProjectHoursCompletion() {
      if (this.projectHoursCompletion.saving) return
      this.projectHoursCompletion.item = null
      this.projectHoursCompletion.hours = null
      this.projectHoursCompletion.mode = 'complete'
      this.projectHoursCompletion.editingHourId = null
      this.projectHoursCompletion.date = ''
      this.projectHoursCompletion.description = ''
    },
    saveProjectHours() {
      var item = this.projectHoursCompletion.item
      if (!item || !this.canSubmitProjectHours || this.projectHoursCompletion.saving) return

      this.projectHoursCompletion.saving = true
      var payload = { hours: this.projectHoursCompletion.hours, date: this.projectHoursCompletion.date, description: this.projectHoursCompletion.description, hours_date: this.projectHoursCompletion.date, hours_description: this.projectHoursCompletion.description }
      var request = this.projectHoursCompletion.mode == 'complete'
        ? serviceAPI.completarTarea(item.id, payload)
        : this.projectHoursCompletion.mode == 'edit'
          ? serviceAPI.editarHorasTarea(item.id, this.projectHoursCompletion.editingHourId, payload)
          : serviceAPI.agregarHorasTarea(item.id, payload)

      request
        .then(response => {
          this.upsertItem(response.data)
          this.projectHoursCompletion.saving = false
          this.projectHoursCompletion.active = false
        })
        .catch(error => {
          this.projectHoursCompletion.saving = false
          this.$awn.alert(Error.showError(error))
        })
    },
    unlinkProjectHours(item, hour) {
      this.confirmDialog('¿Desvincular ' + hour.hours + ' hs de ' + this.taskLabel(item) + '? El registro seguirá disponible en Horas del proyecto.', { title: 'Desvincular horas', icon: 'link_off', okTitle: 'DESVINCULAR' }).then(confirmed => {
        if (!confirmed) return
        serviceAPI.desvincularHorasTarea(item.id, hour.id).then(response => this.upsertItem(response.data)).catch(error => this.$awn.alert(Error.showError(error)))
      })
    },
    removeProjectHours(item, hour) {
      this.confirmDialog('¿Eliminar definitivamente el registro de ' + hour.hours + ' hs de ' + this.taskLabel(item) + '?', { title: 'Eliminar horas', icon: 'delete', danger: true, okTitle: 'ELIMINAR' }).then(confirmed => {
        if (!confirmed) return
        serviceAPI.eliminarHorasTarea(item.id, hour.id).then(response => this.upsertItem(response.data)).catch(error => this.$awn.alert(Error.showError(error)))
      })
    },
    archiveTask(item) {
      this.confirmDialog('¿Archivar la tarea "' + this.taskLabel(item) + '"?', {
        title: 'Archivar tarea',
        icon: 'archive',
        okTitle: 'ARCHIVAR',
      })
        .then(confirmed => {
          if (!confirmed) return
          serviceAPI.archivarTarea(item.id)
            .then(response => {
              this.upsertItem(response.data)
            })
            .catch(error => this.$awn.alert(Error.showError(error)))
        })
        .catch(() => {})
    },
    restoreTask(item) {
      this.confirmDialog('¿Restaurar la tarea "' + this.taskLabel(item) + '"?', {
        title: 'Restaurar tarea',
        icon: 'undo',
        okTitle: 'RESTAURAR',
      })
        .then(confirmed => {
          if (!confirmed) return
          serviceAPI.restaurarTarea(item.id)
            .then(response => {
              this.upsertItem(response.data)
            })
            .catch(error => this.$awn.alert(Error.showError(error)))
        })
        .catch(() => {})
    },
    removeTask(item) {
      this.confirmDialog('¿Eliminar la tarea "' + this.taskLabel(item) + '"?', {
        title: 'Eliminar tarea',
        icon: 'delete',
        danger: true,
        okTitle: 'ELIMINAR',
      })
        .then(confirmed => {
          if (!confirmed) return
          serviceAPI.eliminarTarea(item.id)
            .then(() => {
              if (this.itemSelected && this.itemSelected.id == item.id) this.detailDrawer = false
              this.load()
              this.loadOptions()
            })
            .catch(error => this.$awn.alert(Error.showError(error)))
        })
        .catch(() => {})
    },
    confirmDialog(message, options) {
      var danger = !!options.danger
      var success = !!options.success
      var icon = options.icon || (danger ? 'warning' : (success ? 'check_circle' : 'help'))
      var title = this.$createElement('span', { class: 'app-confirm-header__title' }, [
        this.$createElement('AppIcon', { props: { name: icon } }),
        options.title,
      ])
      return this.$bvModal.msgBoxConfirm(message, {
        title: title,
        size: 'sm',
        buttonSize: 'sm',
        okVariant: danger ? 'danger' : (success ? 'success' : 'dark'),
        okTitle: options.okTitle,
        cancelTitle: options.cancelTitle || 'Cancelar',
        footerClass: 'p-2',
        headerClass: danger ? 'app-confirm-header app-confirm-header--danger' : (success ? 'app-confirm-header task-confirm-header--success' : 'app-confirm-header'),
        hideHeaderClose: false,
        centered: true,
      })
    },
    // RichEditorImages ya normaliza y aplica el límite de imágenes antes
    // de emitir load-image.
    loadImages(images) {
      this.form.images = images
    },
    openDetail(item, index, event) {
      if (event && event.target && event.target.closest && event.target.closest('button,a,.dropdown')) return
      this.itemSelected = item
      this.detailDrawer = true
    },
    goPrevTask() {
      if (!this.canGoPrevTask) return
      var item = this.items[this.currentTaskIndex - 1]
      if (item) this.openDetail(item)
    },
    goNextTask() {
      if (!this.canGoNextTask) return
      var item = this.items[this.currentTaskIndex + 1]
      if (item) this.openDetail(item)
    },
    rowClass(item, type) {
      if (type !== 'row' || !item) return ''
      return this.itemSelected && this.itemSelected.id === item.id ? 'app-table-row-active' : ''
    },
    upsertItem(updated) {
      var index = this.items.findIndex(item => item.id == updated.id)
      if (index === -1) {
        this.load()
        return
      } else if (this.matchesCurrentFilters(updated)) {
        this.$set(this.items, index, updated)
      } else {
        this.items.splice(index, 1)
        this.load()
      }

      this.sortItemsByPriority()
      if (this.itemSelected && this.itemSelected.id == updated.id) this.itemSelected = updated
      this.loadOptions()
    },
    sortItemsByPriority() {
      var priorityRank = { urgent: 1, high: 2, medium: 3, low: 4 }
      this.items.sort((a, b) => {
        var priorityDifference = (priorityRank[a.priority] || 5) - (priorityRank[b.priority] || 5)
        if (priorityDifference) return priorityDifference
        var sortDifference = Number(a.sort_order || 0) - Number(b.sort_order || 0)
        if (sortDifference) return sortDifference
        return new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      })
    },
    canManuallyOrderTask(item) {
      return this.canEditTask(item) && !!(item.customer_id || item.supplier_id || item.project_id || this.detectRelationMode(item) == 'personal')
    },
    orderScopeItems(item) {
      return this.items.filter(current => !current.done && !current.archived && this.isSameOrderScope(current, item))
    },
    hasMovableTask(item) {
      return this.orderScopeItems(item).length > 1
    },
    canMoveTask(item, direction) {
      var scopeItems = this.orderScopeItems(item)
      var index = scopeItems.findIndex(current => current.id == item.id)
      return direction == 'up' ? index > 0 : index !== -1 && index < scopeItems.length - 1
    },
    moveTask(item, direction) {
      if (!this.canManuallyOrderTask(item) || !this.canMoveTask(item, direction) || this.movingTaskId) return

      this.movingTaskId = item.id
      serviceAPI.moverTarea(item.id, direction)
        .then(() => {
          this.movingTaskId = null
          // El backend renumera toda la prioridad del contexto. Recargar la
          // consulta evita que un swap optimista difiera del orden persistido.
          this.load()
        })
        .catch(error => {
          this.movingTaskId = null
          this.$awn.alert(Error.showError(error))
        })
    },
    moveItemInCurrentList(item, direction) {
      var sourceIndex = this.items.findIndex(current => current.id == item.id)
      if (sourceIndex === -1) return

      var candidates = this.items
        .map((current, index) => ({ current: current, index: index }))
        .filter(entry => this.isSameOrderScope(entry.current, item))
      var scopeIndex = candidates.findIndex(entry => entry.current.id == item.id)
      var targetScopeIndex = direction == 'up' ? scopeIndex - 1 : scopeIndex + 1
      if (!candidates[targetScopeIndex]) return

      var targetIndex = candidates[targetScopeIndex].index
      var target = this.items[targetIndex]
      this.$set(this.items, sourceIndex, target)
      this.$set(this.items, targetIndex, item)
    },
    isSameOrderScope(item, reference) {
      if (item.priority != reference.priority) return false
      if (reference.customer_id) return item.customer_id == reference.customer_id
      if (reference.supplier_id) return item.supplier_id == reference.supplier_id
      if (reference.project_id) return item.project_id == reference.project_id
      return this.detectRelationMode(item) == 'personal' && item.assigned_staff_id == reference.assigned_staff_id
    },
    matchesCurrentFilters(item) {
      if (this.filters.archiveStatus == 'active' && item.archived) return false
      if (this.filters.archiveStatus == 'archived' && !item.archived) return false
      if (this.filters.completionStatus == 'pending' && item.done) return false
      if (this.filters.completionStatus == 'completed' && !item.done) return false
      if (this.filters.relationType && this.detectRelationMode(item) != this.filters.relationType) return false
      if (this.filters.category && item.category != this.filters.category) return false
      if (this.filters.priority && item.priority != this.filters.priority) return false
      if (this.optionId(this.filters.customer) && item.customer_id != this.optionId(this.filters.customer)) return false
      if (this.optionId(this.filters.supplier) && item.supplier_id != this.optionId(this.filters.supplier)) return false
      if (this.optionId(this.filters.project) && item.project_id != this.optionId(this.filters.project)) return false
      if (this.optionId(this.filters.assigned) && item.assigned_staff_id != this.optionId(this.filters.assigned)) return false
      return true
    },
    cancelModal() {
      this.confirmDialog('¿Salir sin guardar los cambios de la tarea?', {
        title: 'Cancelar',
        okTitle: 'SALIR',
      }).then(confirmed => {
        if (confirmed) {
          var returnTarget = this.modal.returnTarget
          this.modal.active = false
          if (returnTarget) this.$nextTick(() => this.returnToRelatedDetail(returnTarget))
        }
      })
    },
    resetModal() {
      this.modal.id = null
      this.modal.saving = false
      this.modal.duplicate = false
      this.modal.changeRelation = false
      this.modal.returnTarget = null
      this.modal.relationMode = 'personal'
      this.availableFormCategories = []
      this.formCategoryRequestId++
      this.formKey++
      this.form = {
        title: '',
        description: '',
        category: null,
        priority: 'low',
        assigned: null,
        assigned_staff_id: 0,
        customer: null,
        customer_id: 0,
        supplier: null,
        supplier_id: 0,
        project: null,
        project_id: 0,
        images: [],
        files: [],
      }
    },
    taskTitle(item) {
      return item ? (item.title || '') : ''
    },
    taskLabel(item) {
      var id = item && item.id ? '#' + item.id : ''
      var title = this.taskTitle(item)
      return id && title ? id + ' · ' + title : (id || title)
    },
    taskDescriptionText(item) {
      return item && item.description ? this.truncateText(item.description.replace(/<[^>]*>/g, ' '), 120) : ''
    },
    hasChecklist(item) {
      return item && item.checklist_progress !== null && item.checklist_progress !== undefined
    },
    checklistStatusLabel(status) {
      if (status == 'empty') return 'Checklist sin marcar'
      if (status == 'partial') return 'Checklist en progreso'
      if (status == 'complete') return 'Checklist completo'
      return ''
    },
    checklistStatusIcon(status) {
      if (status == 'empty') return 'warning'
      if (status == 'partial') return 'pending'
      if (status == 'complete') return 'check_circle'
      return ''
    },
    // ChecklistViewer no persiste nada por su cuenta -- emite el html
    // actualizado y acá se guarda con el service propio de tareas.
    onDescriptionChange(newDescription) {
      if (this.checklistSaving) return

      this.checklistSaving = true
      serviceAPI.editarTarea(this.itemSelected.id, { description: newDescription })
        .then(response => {
          this.upsertItem(response.data)
          this.checklistSaving = false
        })
        .catch(error => {
          this.checklistSaving = false
          this.$awn.alert(Error.showError(error))
        })
    },
    priorityOptionFor(priority) {
      return this.priorityOptions.find(option => option.value == priority) || this.priorityOptions[this.priorityOptions.length - 1]
    },
    // Cambio rápido de prioridad desde el sidebar de detalle -- se guarda
    // al toque, sin pasar por el modal de edición.
    changePriority(item, value) {
      if (!this.canEditTask(item) || this.prioritySaving || item.priority == value) return

      this.prioritySaving = true
      serviceAPI.editarTarea(item.id, { priority: value })
        .then(response => {
          this.upsertItem(response.data)
          this.prioritySaving = false
        })
        .catch(error => {
          this.prioritySaving = false
          this.$awn.alert(Error.showError(error))
        })
    },
    taskImages(item) {
      return item && Array.isArray(item.images) ? item.images : []
    },
    taskFiles(item) {
      return item && Array.isArray(item.files) ? item.files : []
    },
    fileSize(bytes) {
      var value = Number(bytes || 0)
      if (!value) return ''
      if (value < 1024 * 1024) return (value / 1024).toFixed(value < 1024 * 100 ? 0 : 1) + ' KB'
      return (value / (1024 * 1024)).toFixed(1) + ' MB'
    },
    isAudioFile(file) {
      if (!file) return false
      if (file.mime && file.mime.indexOf('audio/') === 0) return true
      return /\.(mp3|wav|ogg|oga|m4a|aac|flac|webm)$/i.test(file.name || '')
    },
    taskReminder(item) {
      return item && item.reminder ? item.reminder : null
    },
    taskReminderDescription(item) {
      return item && item.description ? item.description.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim() : ''
    },
    optionId(option) {
      if (option && typeof option == 'object') return option.code || option.id || ''
      return option || ''
    },
    findOption(options, id) {
      if (!id) return null
      return options.find(item => item.code == id) || null
    },
    detectRelationMode(item) {
      if (item.customer_id) return 'customer'
      if (item.supplier_id) return 'supplier'
      if (item.project_id) return 'project'
      return 'personal'
    },
    isContextNodeActive(node) {
      if (this.contextType == 'mine') {
        return this.filters.relationType == node.id &&
          this.filters.archiveStatus == 'active' &&
          this.filters.completionStatus == 'pending' &&
          (node.id == 'personal' || this.optionId(this.filters.assigned) == this.currentStaffId)
      }
      if (this.contextType == 'customers') return this.optionId(this.filters.customer) == node.id
      if (this.contextType == 'suppliers') return this.optionId(this.filters.supplier) == node.id
      if (this.contextType == 'projects') return this.optionId(this.filters.project) == node.id
      if (this.contextType == 'assigned') return this.optionId(this.filters.assigned) == node.id
      if (this.contextType == 'categories') return this.filters.category == node.id
      return false
    },
    staffName(staff) {
      return staff && staff.name ? staff.name : 'Sin asignar'
    },
    staffImage(staff) {
      return staff && staff.image ? staff.image : ''
    },
    taskResponsible(item) {
      return item && item.assigned ? item.assigned : (item ? item.creator : null)
    },
    onCloseTaskComments() {
      this.showTaskComments = false
      this.loadTaskCommentsCount(this.itemSelected)
    },
    loadTaskCommentsCount(item) {
      this.taskCommentsCount = null
      if (!item) return

      messagesServiceAPI.searchReferences('task', item.id)
        .then((response) => {
          if (!this.itemSelected || this.itemSelected.id != item.id) return
          this.taskCommentsCount = (response.data || []).length
        })
        .catch(() => {
          // silencioso: si falla, el botón de Comentarios queda sin badge en vez de romper el sidebar.
        })
    },
    canEditTask(item) {
      return !!(item && !item.done && !item.archived)
    },
    canCompleteTask(item) {
      return !!(item && this.managesStatus(item) && item.assigned_staff_id && Number(item.assigned_staff_id) === Number(this.currentStaffId))
    },
    canAddProjectHours(item) {
      return !!(item && item.project_id && !item.archived)
    },
    canManageProjectHours(item) {
      return !!(item && item.project_id)
    },
    canManageProjectHour(item, hour) {
      return this.canManageProjectHours(item) && hour && hour.employee && Number(hour.employee.staff_id) === Number(this.currentStaffId) && this.hourBillingStatus(hour) == 'unbilled'
    },
    hourBillingStatus(hour) {
      if (hour && hour.sale_id) return 'invoiced'
      if (hour && hour.not_invoice) return 'not-invoiced'
      if (hour && hour.check_for_invoice) return 'pending-invoice'
      return 'unbilled'
    },
    hourBillingLabel(hour) {
      return {
        unbilled: 'Sin facturar',
        invoiced: 'Facturada',
        'not-invoiced': 'No facturable',
        'pending-invoice': 'Por facturar',
      }[this.hourBillingStatus(hour)]
    },
    taskProjectHours(item) {
      return item && Array.isArray(item.project_hours) ? item.project_hours : []
    },
    hourStaff(hour) {
      return hour && hour.employee && hour.employee.staff ? hour.employee.staff : null
    },
    formatHourDate(value) {
      return value ? moment(value).format('DD/MM/YYYY') : 'Sin fecha'
    },
    projectHoursLabel(item) {
      var count = Number(item && item.project_hours_count || 0)
      var total = Number(item && item.project_hours_total || 0)
      if (!count) return 'Sin cargar'
      return total + ' hs · ' + count + (count === 1 ? ' registro' : ' registros')
    },
    managesStatus(item) {
      return !item || item.manage_status !== false
    },
    avatarFromName(name) {
      return Funciones.avatarFromName(name || '')
    },
    relationTitle(item) {
      if (item.customer_id) return 'Cliente'
      if (item.supplier_id) return 'Proveedor'
      if (item.project_id) return 'Proyecto'
      return 'Tipo'
    },
    relationValue(item) {
      if (item.customer && item.customer.name) return item.customer.name
      if (item.supplier && item.supplier.name) return item.supplier.name
      if (item.project && item.project.name) return item.project.name
      return 'Personal'
    },
    relationEntity(item) {
      if (item.customer_id && item.customer) return item.customer
      if (item.supplier_id && item.supplier) return item.supplier
      return null
    },
    relationIcon(item) {
      if (item.project_id) return 'folder'
      if (item.supplier_id) return 'local_shipping'
      return 'person'
    },
    statusLabel(item) {
      if (!this.managesStatus(item)) return 'Sin estado'
      if (item.archived) return 'Archivada'
      return item.done ? 'Completada' : 'Pendiente'
    },
    statusVariant(item) {
      if (!this.managesStatus(item)) return 'secondary'
      if (item.archived) return 'secondary'
      return item.done ? 'success' : 'warning'
    },
    formatDate(date) {
      return date ? moment(date).format('DD/MM/YYYY HH:mm') : ''
    },
    humanDate(date) {
      return date ? moment(date).fromNow() : ''
    },
    truncateText(value, length) {
      value = (value || '').replace(/\s+/g, ' ').trim()
      return value.length > length ? value.slice(0, length - 1) + '…' : value
    },
  },
}
</script>

<style>
.task-list-view {
  width: 100%;
}
.task-confirm-header--success {
  background: var(--app-color-success-soft) !important;
  border-bottom: 2px solid var(--app-color-success) !important;
}
.task-confirm-header--success .app-confirm-header__title {
  color: var(--app-color-success) !important;
}
.task-filter-search {
  width: 220px;
}
.task-filter-status {
  width: 150px;
}
.task-filter-type,
.task-filter-priority {
  width: 150px;
}
.task-filter-category {
  width: 180px;
}
.task-filter-assigned {
  width: 200px;
}
.task-filter-relation {
  width: 220px;
}
.task-filter-apply {
  width: 31px;
  height: 31px;
  flex-shrink: 0;
  padding: 0;
}
.task-context-alert {
  margin: 10px 0 0;
  padding: 8px 12px;
  font-size: 12px;
}
.task-workspace {
  display: flex;
  align-items: stretch;
  gap: 12px;
  /* Alto = el del contenedor de tareas (max pantalla / página de tareas),
     medido en JS e inyectado como --task-ws-h. Con align-items:stretch ambas
     columnas quedan exactamente a ese alto. Fallback mientras no hay medición. */
  min-height: var(--task-min-h, 100vh);
  height: var(--task-ws-h, auto);
}
.task-context-panel {
  display: flex;
  flex-direction: column;
  flex: 0 0 280px;
  min-height: 0;
  /* Igual alto que el contenedor de tareas (lo impone el workspace); si la
     lista de contextos lo excede, scrollea dentro (.task-context-list). */
  overflow: hidden;
  margin-top: 8px;
  border: 1px solid var(--app-color-border);
  border-radius: 4px;
  background: var(--app-color-surface);
}
.task-context-panel__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 12px;
  border-bottom: 1px solid var(--app-color-border);
  color: var(--app-color-text);
  font-size: 13px;
  font-weight: 700;
}
.task-context-panel__header span {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
.task-context-panel__toggle {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 27px;
  height: 27px;
  padding: 0;
  color: var(--app-color-text-muted);
}
.task-context-tabs {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 4px;
  padding: 8px;
  border-bottom: 1px solid var(--app-color-border);
  background: var(--app-color-surface-subtle);
}
.task-context-tabs__btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 30px;
  border: 1px solid transparent;
  border-radius: 4px;
  background: transparent;
  color: var(--app-color-text-muted);
}
.task-context-tabs__btn:hover,
.task-context-tabs__btn--active {
  border-color: var(--app-color-primary);
  background: var(--app-color-primary-soft);
  color: var(--app-color-primary);
}
.task-context-search {
  padding: 8px;
  border-bottom: 1px solid var(--app-color-border);
}
.task-context-list {
  flex: 1 1 auto;
  min-height: 0;
  overflow: auto;
  padding: 6px;
}
.task-context-list {
  scrollbar-width: thin;
  scrollbar-color: var(--app-color-border-strong) transparent;
}
.task-context-list::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}
.task-context-list::-webkit-scrollbar-track {
  background: transparent;
}
.task-context-list::-webkit-scrollbar-thumb {
  border: 2px solid transparent;
  border-radius: 999px;
  background-clip: padding-box;
  background-color: var(--app-color-border-strong);
}
.task-context-list::-webkit-scrollbar-thumb:hover {
  background-color: var(--app-color-text-muted);
}
.task-context-loader,
.task-table-loader {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 84px;
  color: var(--app-color-text-muted);
  font-size: 12px;
}
.task-table-loader {
  min-height: 180px;
}
.task-context-node {
  display: flex;
  align-items: center;
  width: 100%;
  min-height: 46px;
  gap: 8px;
  padding: 8px;
  border: 0;
  border-left: 3px solid transparent;
  border-radius: 4px;
  background: transparent;
  color: var(--app-color-text);
  font-size: 12px;
  text-align: left;
}
.task-context-node:hover,
.task-context-node--active {
  border-left-color: var(--app-color-primary);
  background: var(--app-color-primary-soft);
  color: var(--app-color-primary);
}
.task-context-node--urgent {
  border-left-color: var(--app-color-danger-border);
  background: var(--app-color-danger-soft);
}
.task-context-node--urgent:hover,
.task-context-node--urgent.task-context-node--active {
  border-left-color: var(--app-color-danger);
  background: var(--app-color-danger-soft);
  color: var(--app-color-text);
}
.task-context-node__icon,
.task-context-node__avatar {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  flex: 0 0 28px;
  border-radius: 4px;
}
.task-context-node__icon {
  background: var(--app-color-surface-subtle);
  color: var(--app-color-text-muted);
}
.task-context-node--active .task-context-node__icon,
.task-context-node:hover .task-context-node__icon {
  background: var(--app-color-surface);
  color: var(--app-color-primary);
}
.task-context-node__body {
  display: flex;
  min-width: 0;
  flex: 1;
  flex-direction: column;
}
.task-context-node__body strong,
.task-context-node__body small {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.task-context-node__body strong {
  font-size: 12px;
  font-weight: 700;
}
.task-context-node__body small {
  color: var(--app-color-text-muted);
  font-size: 10px;
}
.task-context-node__urgent-icon {
  margin-left: 2px;
  color: var(--app-color-danger);
  font-size: 12px;
  vertical-align: -1px;
}
.task-context-node > .badge {
  min-width: 22px;
  padding: 4px 7px;
  font-size: 12px;
}
.task-context-node > .task-context-node__count {
  border: 1px solid var(--app-color-primary-border);
  background: var(--app-color-primary-soft);
  color: var(--app-color-primary);
  font-weight: 700;
}
.task-context-collapsed {
  display: flex;
  flex: 0 0 34px;
  min-height: 260px;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-top: 8px;
  border: 1px solid var(--app-color-border);
  border-radius: 4px;
  background: var(--app-color-surface-subtle);
  color: var(--app-color-primary);
  cursor: pointer;
  writing-mode: vertical-rl;
}
.task-results {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
}
.task-results-card {
  display: flex;
  flex-direction: column;
  /* Llena el alto del workspace (que ya vale max(pantalla, página de tareas)).
     Como ese alto nunca es menor que el contenido, la tabla entra completa sin
     scroll vertical propio. */
  flex: 1;
  min-height: 0;
  overflow: hidden;
}
.task-results-card > .table-responsive {
  flex: 1 1 auto;
  min-height: 0;
  /* Bootstrap agrega 1rem para separar tablas normales. Acá la tabla vive
     dentro de una card de alto calculado: ese margen queda fuera de la medida
     y puede ocultar la última fila. */
  margin-bottom: 0;
  /* La altura del workspace se calcula a partir de todas las filas de la
     página. La tabla puede desplazarse sólo en horizontal en pantallas
     angostas; nunca genera un segundo scroll vertical. */
  overflow-x: auto;
  overflow-y: hidden;
}
.task-results--expanded {
  width: calc(100% - 46px);
}
.task-results .app-table thead th,
.task-results .app-table tbody td {
  vertical-align: middle;
}
.task-results .app-table thead th:first-child,
.task-results .app-table tbody td:first-child {
  padding-left: 16px;
}
.task-results .app-table thead th:last-child,
.task-results .app-table tbody td:last-child {
  padding-right: 12px;
}
.task-title-cell {
  display: flex;
  min-width: 190px;
  padding-left: 12px;
  flex-direction: column;
}
.task-title-cell__heading {
  display: flex;
  min-width: 0;
  align-items: center;
  gap: 8px;
}
.task-title-cell__heading strong {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.task-title-cell__icons {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  flex: 0 0 auto;
  color: var(--app-color-text-muted);
  font-size: 12px;
  font-weight: 700;
}
.task-title-cell__icons--reminder {
  color: var(--app-color-primary);
}
.task-reminder-stat--active {
  border-color: var(--app-color-primary-border);
  background: var(--app-color-primary-soft);
}
.task-reminder-stat__value {
  display: inline-flex;
  align-items: center;
  gap: 5px;
}
.task-reminder-stat__value .material-symbols-rounded {
  color: var(--app-color-primary);
  font-size: 17px;
}
.task-reminder-stat__date {
  display: flex;
  flex-direction: column;
  line-height: 1.2;
}
.task-reminder-stat__date strong {
  font-weight: 700;
}
.task-reminder-stat__date small {
  color: var(--app-color-text-secondary);
  font-size: 11px;
}
.task-title-cell small {
  display: block;
  overflow: hidden;
  color: var(--app-color-text-muted);
  text-overflow: ellipsis;
  white-space: nowrap;
}
.task-category-pill {
  display: inline-flex;
  max-width: 150px;
  min-width: 58px;
  align-items: center;
  justify-content: center;
  padding: 2px 8px;
  border-radius: 999px;
  background: var(--app-color-primary-soft);
  color: var(--app-color-primary);
  font-size: 12px;
  font-weight: 700;
}
.task-table-priority-col {
  width: 40px;
}
.task-table-progress-col {
  width: 94px;
}
.task-checklist-progress {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-width: 70px;
  color: var(--app-color-text-secondary);
  font-size: var(--app-text-xs);
  font-weight: var(--app-font-bold);
}
.task-checklist-progress .progress {
  width: 44px;
  background: var(--app-color-border);
}
.task-checklist-progress--empty .progress-bar { background: var(--app-color-danger); }
.task-checklist-progress--partial .progress-bar { background: var(--app-color-warning); }
.task-checklist-progress--complete .progress-bar { background: var(--app-color-success); }
.task-priority-dot {
  display: inline-flex;
  width: 22px;
  height: 22px;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  color: #fff;
  font-size: 12px;
}
.task-priority-dot--urgent {
  background: var(--app-color-danger);
}
.task-priority-dot--high {
  background: var(--app-color-warning);
}
.task-priority-dot--medium {
  background: var(--app-color-primary);
}
.task-priority-dot--low {
  background: var(--app-color-text-secondary);
}
.task-relation-cell {
  display: flex;
  min-width: 0;
  max-width: 280px;
  align-items: center;
  gap: 8px;
}
.task-relation-cell .b-avatar,
.task-relation-cell__icon {
  flex: 0 0 auto;
}
.task-relation-cell__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 4px;
  background: var(--app-color-surface-subtle);
  color: var(--app-color-text-muted);
}
.task-relation-cell__body {
  display: flex;
  min-width: 0;
  flex: 1;
  flex-direction: column;
}
.task-relation-cell__body small,
.task-relation-cell__body strong {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.task-relation-cell__body small {
  color: var(--app-color-text-muted);
  font-size: 11px;
  line-height: 1.1;
}
.task-relation-cell__body strong {
  color: var(--app-color-text);
  font-size: 13px;
  line-height: 1.2;
}
.task-people-cell {
  display: flex;
  min-width: 120px;
  max-width: 170px;
  flex-direction: row;
  gap: 6px;
  color: var(--app-color-text-muted);
  font-size: 13px;
}
.task-people-cell span {
  display: inline-flex;
  min-width: 0;
  align-items: center;
  gap: 4px;
  color: var(--app-color-text);
  font-weight: 600;
}
.task-people-cell .b-avatar {
  flex: 0 0 auto;
}
/* .task-people-cell span (abajo) le pone color oscuro a TODO span dentro
   de la celda, para el nombre del responsable -- pero la inicial del
   avatar también vive en un <span> (BootstrapVue anida <span
   class="b-avatar-text"><span>S</span></span>, y ese span interno no
   tiene clase propia), así que ese mismo color oscuro le pisaba el
   blanco que ya trae por :style desde avatarFromName(), dejando letra
   oscura sobre fondo oscuro. Hace falta más especificidad que
   ".task-people-cell span" para ganarle, y cubrir tanto el span
   contenedor como el span interno con el texto real. */
.task-people-cell .b-avatar span {
  color: #fff;
}
/* Anillo oscuro sutil (no el color de la superficie: la tabla YA es
   blanca, un anillo blanco sobre blanco es invisible) para separar el
   círculo del fondo sea cual sea el color que le tocó por hash, y la
   inicial en negrita mejora la legibilidad. */
.task-list-view .b-avatar {
  box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.18);
}
.task-list-view .b-avatar-text {
  font-weight: 700;
}
.task-people-cell span {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.task-date-cell {
  display: flex;
  min-width: 100px;
  flex-direction: column;
}
.task-date-cell strong {
  color: var(--app-color-text);
  font-size: 13px;
  font-weight: 700;
}
.task-date-cell small {
  color: var(--app-color-text-muted);
  font-size: 11px;
}
/* En mobile el kebab se conserva con texto visible ("Opciones") en vez de
   ícono mudo -- ver AppIcon: su prop `label` solo setea aria-label, no
   pinta texto en pantalla, así que el "Opciones" viene del <span> hermano
   en el template, y este botón tiene que verse como link, no como caja. */
.btn.task-action-toggle-labeled {
  display: inline-flex !important;
  align-items: center;
  justify-content: flex-start;
  width: auto !important;
  height: auto !important;
  min-height: 0;
  padding: 0 !important;
  border: none !important;
  background: transparent !important;
  box-shadow: none !important;
  color: var(--app-color-text-secondary) !important;
  font-size: 13px;
  line-height: 1.15;
}
.btn.task-action-toggle-labeled:hover,
.btn.task-action-toggle-labeled:focus {
  color: var(--app-color-primary) !important;
  text-decoration: underline;
}
.task-table-footer {
  flex: 0 0 auto;
  display: flex;
  min-height: 42px;
  align-items: center;
  justify-content: space-between;
  padding: 0 14px;
}
.task-sidebar-title {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
.task-detail {
  padding: 14px;
}
/* .list-view-drawer__stat--warning ya es global (_list-views.scss), pero
   solo cubre pendiente -- acá se agregan los otros dos estados que usa
   statusVariant() en la tabla (completada/archivada), para que el tile
   "Estado" del sidebar use exactamente los mismos colores que la tabla. */
.task-detail .list-view-drawer__stat--success {
  border-color: var(--app-color-success-border);
  background: var(--app-color-success-soft);
}
.task-detail .list-view-drawer__stat--success .list-view-drawer__stat-value {
  color: var(--app-color-success);
}
.task-detail .list-view-drawer__stat--secondary {
  border-color: var(--app-color-border-strong);
  background: var(--app-color-surface-subtle);
}
.task-detail .list-view-drawer__stat--secondary .list-view-drawer__stat-value {
  color: var(--app-color-text-secondary);
}
.task-detail__quick-action {
  margin-top: 12px;
}
.task-detail__quick-action .btn {
  min-height: 38px;
  font-weight: 700;
}
/* Fecha/hora exacta debajo de la fecha en lenguaje humano (Fechas: Creada,
   Completada, Archivada), más chica y muted para no competir con ella. */
.task-detail .task-detail-date-sub {
  margin-top: 2px;
  font-size: 11px;
  color: var(--app-color-text-secondary);
}
/* Las secciones del sidebar (Contexto, Personas, Fechas, etc.) y los
   labels de cada campo (Proyecto, Categoría, Responsable...) comparten
   clases globales (.list-view-drawer__section-title /
   .app-modal-detail-list__label) con estilos casi idénticos -- se ven
   iguales y cuesta distinguir "título de sección" de "label de campo".
   Se refuerza acá, scopeado a .task-detail, para no tocar esas clases
   globales (las usan 37+ vistas en todo el sistema). */
.task-detail .list-view-drawer__section-title {
  margin-top: 18px;
  margin-bottom: 8px;
  padding-bottom: 6px;
  border-bottom: 1px solid var(--app-color-border);
  color: var(--app-color-text);
  font-size: 12px;
  font-weight: 800;
}
.task-detail .list-view-drawer__section-title:first-of-type {
  margin-top: 0;
}
.task-detail .list-view-drawer__section-title .app-icon {
  color: var(--app-color-primary);
}
.task-checklist-alert {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 8px;
  padding: 4px 10px;
  border: 1px solid transparent;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 700;
}
.task-checklist-alert--empty {
  border-color: var(--app-color-danger-border);
  background: var(--app-color-danger-soft);
  color: var(--app-color-danger);
}
.task-checklist-alert--partial {
  border-color: var(--app-color-warning-border);
  background: var(--app-color-warning-soft);
  color: var(--app-color-warning-hover);
}
.task-checklist-alert--complete {
  border-color: var(--app-color-success-border);
  background: var(--app-color-success-soft);
  color: var(--app-color-success);
}
.task-detail__description {
  overflow: hidden;
  padding: 12px;
  border: 1px solid var(--app-color-border);
  border-radius: 4px;
  background: var(--app-color-surface-subtle);
  color: var(--app-color-text);
  font-size: 13px;
  line-height: 1.5;
}
.task-detail__description img {
  max-width: 100%;
  height: auto;
  border-radius: 4px;
}
.task-detail__images,
.task-image-preview {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 10px;
}
.task-detail__images img,
.task-image-preview__item img {
  width: 94px;
  height: 94px;
  border: 1px solid var(--app-color-border);
  border-radius: 4px;
  object-fit: cover;
}
.task-detail__files {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-top: 10px;
}
.task-detail__file,
.task-detail__files a {
  display: flex;
  align-items: center;
  gap: 7px;
  min-width: 0;
  padding: 8px 10px;
  border: 1px solid var(--app-color-border);
  border-radius: 4px;
  background: var(--app-color-surface);
  color: var(--app-color-text);
  font-size: 12px;
}
.task-detail__file {
  padding: 8px 10px;
  flex-wrap: wrap;
}
.task-detail__files a {
  padding: 0;
  border: 0;
}
.task-detail__files a:hover {
  border-color: var(--app-color-primary);
  color: var(--app-color-primary);
  text-decoration: none;
}
.task-detail__files span {
  overflow: hidden;
  flex: 1;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.task-detail__files small {
  color: var(--app-color-text-muted);
}
.task-detail__files audio {
  display: block;
  width: 100%;
  height: 32px;
  margin-top: 8px;
}
.task-detail-person {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
.task-image-preview__item {
  position: relative;
}
.task-image-preview__item button {
  position: absolute;
  top: 4px;
  right: 4px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border: 0;
  border-radius: 4px;
  background: var(--app-color-danger);
  color: #fff;
}
.task-form {
  display: flex;
  flex-direction: column;
  gap: 14px;
}
.task-form__section {
  padding-bottom: 2px;
}
.task-priority-drawer-dropdown {
  display: block;
  margin-top: 2px;
}
.btn.task-priority-toggle {
  display: flex;
  align-items: center;
  width: 100%;
  height: 30px;
  padding: 0 10px;
  border: none;
  color: #fff;
  font-size: 14px;
  font-weight: 700;
  box-sizing: border-box;
}
.task-priority-toggle__caret {
  margin-left: auto;
  padding-left: 4px;
}
.btn.task-priority-toggle--urgent {
  background: var(--app-color-danger);
}
.btn.task-priority-toggle--urgent:hover,
.btn.task-priority-toggle--urgent:focus {
  background: var(--app-color-danger-hover);
  color: #fff;
}
.btn.task-priority-toggle--high {
  background: var(--app-color-warning);
}
.btn.task-priority-toggle--high:hover,
.btn.task-priority-toggle--high:focus {
  background: var(--app-color-warning-hover);
  color: #fff;
}
.btn.task-priority-toggle--medium {
  background: var(--app-color-primary);
}
.btn.task-priority-toggle--medium:hover,
.btn.task-priority-toggle--medium:focus {
  background: var(--app-color-primary-hover);
  color: #fff;
}
.btn.task-priority-toggle--low {
  background: var(--app-color-text-secondary);
}
.btn.task-priority-toggle--low:hover,
.btn.task-priority-toggle--low:focus {
  background: var(--app-color-text);
  color: #fff;
}
.task-priority-menu-item {
  display: flex;
  align-items: center;
  gap: 6px;
  width: 100%;
  padding: 3px 6px;
  border-radius: 4px;
  border-left: 3px solid transparent;
  font-weight: 700;
}
.task-priority-menu-item--urgent {
  color: var(--app-color-danger);
}
.task-priority-menu-item--high {
  color: var(--app-color-warning-hover);
}
.task-priority-menu-item--medium {
  color: var(--app-color-primary);
}
.task-priority-menu-item--low {
  color: var(--app-color-text-secondary);
}
.task-priority-menu-item--active.task-priority-menu-item--urgent {
  background: var(--app-color-danger-soft);
  border-left-color: var(--app-color-danger);
}
.task-priority-menu-item--active.task-priority-menu-item--high {
  background: var(--app-color-warning-soft);
  border-left-color: var(--app-color-warning);
}
.task-priority-menu-item--active.task-priority-menu-item--medium {
  background: var(--app-color-primary-soft);
  border-left-color: var(--app-color-primary);
}
.task-priority-menu-item--active.task-priority-menu-item--low {
  background: var(--app-color-surface-subtle);
  border-left-color: var(--app-color-text-secondary);
}
.task-priority-menu-item__check {
  margin-left: auto;
}
.task-hours-section-title {
  align-items: center;
  display: flex;
  justify-content: space-between;
}
.task-hours-section-title > span {
  align-items: center;
  display: inline-flex;
  gap: 6px;
}
.task-hours-list {
  border: 1px solid var(--app-color-border);
  border-radius: var(--app-radius-sm);
  background: var(--app-color-surface);
  overflow: visible;
  position: relative;
}
.task-hours-list__item {
  align-items: center;
  background: var(--app-color-surface);
  border-bottom: 1px solid var(--app-color-border);
  border-left: 3px solid var(--app-color-primary);
  display: flex;
  gap: 8px;
  justify-content: space-between;
  padding: 8px 10px;
}
.task-hours-list__item:last-child {
  border-bottom: 0;
}
.task-hours-list__copy {
  display: flex;
  flex-direction: column;
  min-width: 0;
}
.task-hours-list__headline {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 7px;
}
.task-hours-list__status {
  border-radius: 999px;
  font-size: 10px;
  font-weight: 700;
  line-height: 1;
  padding: 4px 6px;
}
.task-hours-list__status--unbilled { background: var(--app-color-danger-hover); color: #fff; }
.task-hours-list__status--pending-invoice { background: var(--app-color-warning-hover); color: #fff; }
.task-hours-list__status--invoiced { background: var(--app-color-success-hover); color: #fff; }
.task-hours-list__status--not-invoiced { background: var(--app-color-text); color: #fff; }
.task-hours-list__item small {
  color: var(--app-color-text-muted);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.task-hours-list__author {
  align-items: center;
  color: var(--app-color-text-secondary);
  display: inline-flex;
  font-size: 11px;
  gap: 5px;
  margin-top: 5px;
}
.task-hours-list .dropdown-menu {
  z-index: 1060;
}
.task-hours-modal__header {
  align-items: center;
  display: flex;
  gap: 10px;
  width: 100%;
}
.task-hours-modal__header-icon {
  align-items: center;
  background: var(--app-color-primary-soft);
  border-radius: 50%;
  color: var(--app-color-primary);
  display: inline-flex;
  height: 34px;
  justify-content: center;
  width: 34px;
}
.task-hours-modal__header > span:nth-child(2) { display: flex; flex-direction: column; }
.task-hours-modal__header small { color: var(--app-color-text-muted); font-size: 11px; font-weight: 700; letter-spacing: .03em; text-transform: uppercase; }
.task-hours-modal__header strong { color: var(--app-color-text); font-size: 17px; }
.task-hours-modal__close { color: var(--app-color-text-muted); margin-left: auto; padding: 3px; }
.task-hours-modal__intro { color: var(--app-color-text-secondary); font-size: var(--app-text-sm); line-height: 1.45; margin-bottom: 15px; }
@media (max-width: 767.98px) {
  .task-filter-search,
  .task-filter-status,
  .task-filter-type,
  .task-filter-category,
  .task-filter-priority,
  .task-filter-assigned,
  .task-filter-relation {
    width: 100%;
  }
  .task-workspace {
    flex-direction: column;
    height: auto;
    min-height: 0;
  }
  .task-context-panel,
  .task-results,
  .task-results--expanded {
    width: 100%;
    flex-basis: auto;
  }
  .task-context-panel {
    overflow: visible;
  }
  .task-context-list {
    overflow: visible;
  }
  .task-context-collapsed {
    width: 100%;
    min-height: 34px;
    flex-basis: auto;
    writing-mode: horizontal-tb;
  }
}
</style>
